var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { differenceInYears } from "date-fns";
import { EnrollmentFlowType, EventCategory, mapConsentTypeToEventCategory, } from "healthex-common/analytics.dtos";
import { ConsentStatus, ConsentUIFlow, doesProjectAllowIdentifiedFutureDataUse, getConsentTypesForProject, getDataConsentTypeForProject, getIASConsentTypes, getPrimaryConsentTypeForProject, getPrimaryIASConsentType, invalidPagesForConsentFlowProgressUpdates, isConsentDataSensitivityScopeValidForConsent, } from "healthex-common/consent.dtos";
import { ConsentDataSensitivityScope } from "healthex-common/consentscopes.dtos";
import { maxStudyAdditionalSections } from "healthex-common/constants";
import { DocumentRetrievalAuthType, DocumentRetrievalLocationStatus, DocumentRetrievalTaskStatus, } from "healthex-common/document.retrieval.dtos";
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
import { PreviewConsentFlow, PreviewConsentMode, PreviewParticipantType, PreviewRequestType, } from "healthex-common/preview.dtos";
import { screenshotResourceLoadingClass, ScreenshotWaitStrategy, } from "healthex-common/screenshot.dtos";
import { ProjectType, StudyStatus, StudyTranslationLanguage, StudyType, StudyVersionStatus, } from "healthex-common/study.dtos";
import { getPosterUrl } from "healthex-common/transcode.dtos";
import { usaStateFromName, USAStates } from "healthex-common/usaStates";
import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { getFullName, includes, slugify, unslugify, willNeverHappen } from "healthex-common/utils";
import { computed, observable, reaction, when } from "mobx";
import { PatientConsentSession, PatientConsentSessionState } from "./PatientConsentSession";
import { getObservableT, i18nSetLanguage } from "../Infra/i18next";
import { NOT_LOADED } from "../Infra/RemoteData";
import { SmartAuthErrorReason, SmartAuthState } from "../Logic/SmartAuthManager";
import { NamedRoutes } from "./NamedRoutes";
import { hipaaContentLoadedClassName } from "./PatientConsentHipaaAuthorizationViewModel";
import { patientConsentStepParentId } from "./PatientConsentManagerShared.styles";
import { patientConsentRouteFromParts, patientConsentRouteParts, patientConsentRouteValues, } from "./PatientConsentRoute";
import { MobileBottomNavigationButtonType, PatientStudiesListViewTab, } from "./PatientResearchDashboardUIManager";
import { effectiveQueryParams } from "./queryParams";
import { findMatchingNamedRouteParent, routeEquals } from "./RoutingUIManager";
import { getTranslatedStudyText } from "./TranslationService";
import { getUIHash } from "./uiHash";
import { instanceToPlain, plainToInstance } from "class-transformer";
import { FacilitatedFHIRAuthErrorReason, FacilitatedFHIRAuthState } from "./FacilitatedFHIRAuthManager";
const t = getObservableT("patients");
export var PatientConsentFlow;
(function (PatientConsentFlow) {
    PatientConsentFlow["ENROLLMENT"] = "ENROLLMENT";
})(PatientConsentFlow || (PatientConsentFlow = {}));
export var PatientConsentLink;
(function (PatientConsentLink) {
    PatientConsentLink["LINK"] = "LINK";
})(PatientConsentLink || (PatientConsentLink = {}));
export var PatientConsentExcludedReason;
(function (PatientConsentExcludedReason) {
    PatientConsentExcludedReason["UNDERAGE"] = "UNDERAGE";
    PatientConsentExcludedReason["NOT_IN_UNITED_STATES"] = "NOT_IN_UNITED_STATES";
    PatientConsentExcludedReason["NOT_RECRUITING"] = "NOT_RECRUITING";
    PatientConsentExcludedReason["TRANSLATIONS_NOT_AVAILABLE"] = "TRANSLATIONS_NOT_AVAILABLE";
})(PatientConsentExcludedReason || (PatientConsentExcludedReason = {}));
export class PatientConsentUIManager {
    constructor(authManager, routingUIManager, studyManager, iasManager, patientService, stateLawsService, patientResearchDashboardUIManager, consentExpirationService, smartAuthManager, mrnSearchService, bannerUIManager, analyticsService, browserLanguageUIManager, environment, documentRetrievalManager, persistence, facilitatedFHIRAuthManager) {
        this.authManager = authManager;
        this.routingUIManager = routingUIManager;
        this.studyManager = studyManager;
        this.iasManager = iasManager;
        this.patientService = patientService;
        this.stateLawsService = stateLawsService;
        this.patientResearchDashboardUIManager = patientResearchDashboardUIManager;
        this.consentExpirationService = consentExpirationService;
        this.smartAuthManager = smartAuthManager;
        this.mrnSearchService = mrnSearchService;
        this.bannerUIManager = bannerUIManager;
        this.analyticsService = analyticsService;
        this.browserLanguageUIManager = browserLanguageUIManager;
        this.environment = environment;
        this.documentRetrievalManager = documentRetrievalManager;
        this.persistence = persistence;
        this.facilitatedFHIRAuthManager = facilitatedFHIRAuthManager;
        this.oauthParams = null;
        this.oauthParamsKey = "oauth_params";
        this.iasPath = "ias";
        this._session = new PatientConsentSession();
        this.previewMockedLocationData = this.createPreviewMockedLocationData();
        reaction(() => this.facilitatedFHIRAuthManager.state, () => {
            this.processFacilitatedFHIRAuthState();
        }, { fireImmediately: true });
        reaction(() => this.urlParams.oauthState, () => this.handleOAuthParams(), { fireImmediately: true });
        reaction(() => routingUIManager.currentRoute, async () => {
            var _a;
            const studyId = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.studyId;
            if (this.environment.OPENAI_AI_ENABLEMENT_PROJECT_ID === studyId ||
                this.environment.ANTHROPIC_AI_ENABLEMENT_PROJECT_ID === studyId) {
                this.handleOAuthParams();
            }
        }, { fireImmediately: true });
        reaction(() => [this._session.studyId, this.patientConsentFlow, this.isDataUpdate], async () => {
            if (this._session.patientConsentSessionState !== PatientConsentSessionState.INITIALIZED)
                return;
            this._session.didEnterConsentFlow = false;
            this._session.didTryToRedirectToWelcomeBackPage = false;
            this._session.continueWhereILeftOffRoute = null;
            this._session.retrievalBatchId = null;
        }, { fireImmediately: true });
        reaction(() => this.isPatientLoggedIn, async () => {
            if (this.isPatientLoggedIn) {
                await this.refreshStudyAndPatientInfo();
            }
        }, { fireImmediately: true });
        reaction(() => routingUIManager.currentRoute, async () => {
            this.determineGuidedConsentCloseRoute();
            await this.refreshStudyAndPatientInfo();
        }, { fireImmediately: true });
        reaction(() => this.smartAuthManager.errorReason, async (errorReason) => {
            if (this.smartAuthManager.reauthSuccess === true ||
                this.smartAuthManager.state !== SmartAuthState.ERROR ||
                errorReason === null ||
                errorReason === SmartAuthErrorReason.LOGIN_WINDOW_CLOSED ||
                errorReason === SmartAuthErrorReason.AUTH_FAILED) {
                return;
            }
            await this.saveConsentFlowProgress();
        }, { fireImmediately: false });
        reaction(() => this.currentPage, async (currentPage) => {
            if (!currentPage)
                return;
            if (!currentPage.startsWith("ENROLLMENT_"))
                return;
            if (this.isPrereadEnrollment && currentPage === "ENROLLMENT_PREREAD_THANKYOU")
                return;
            await when(() => this._session.study !== NOT_LOADED);
            await this.trackEvent({
                action: "OPEN",
            });
        }, { fireImmediately: true });
        reaction(() => this.currentPage, async (currentPage) => {
            if (!this.isPrereadEnrollment)
                return;
            if (currentPage && currentPage !== "ENROLLMENT_PREREAD_THANKYOU")
                return;
            await when(() => this._session.study !== NOT_LOADED);
            await this.trackEvent({
                category: EventCategory.PREREAD_FLOW,
                action: "COMPLETE",
            });
        }, { fireImmediately: true });
        reaction(() => this.smartAuthManager.reauthProxyAccess, async (reauthProxyAccess) => {
            if (!this.currentPage)
                return;
            const proxyAccess = reauthProxyAccess === null ? null : { ...reauthProxyAccess };
            if (["PRESCREEN_PARENT_GUARDIAN_REAUTHORIZATION", "ENROLLMENT_PARENT_GUARDIAN_REAUTHORIZATION"].includes(this.currentPage)) {
                this._session.parentGuardianProxyAccess = proxyAccess;
            }
            else if ([
                "PRESCREEN_ADDITIONAL_PARENT_GUARDIAN_REAUTHORIZATION",
                "ENROLLMENT_ADDITIONAL_PARENT_GUARDIAN_REAUTHORIZATION",
            ].includes(this.currentPage)) {
                const additionalParentGuardianName = (proxyAccess === null || proxyAccess === void 0 ? void 0 : proxyAccess.relatedPerson) ? getFullName(proxyAccess.relatedPerson) : "";
                this._session.isAdditionalParentGuardianNameUnique = additionalParentGuardianName !== this.parentGuardianName;
                this._session.additionalParentGuardianProxyAccess = this._session.isAdditionalParentGuardianNameUnique
                    ? proxyAccess
                    : null;
            }
        }, { fireImmediately: false });
        this.reactiveRedirect(NamedRoutes.PATIENT_CONSENT_ENROLLMENT, async (routeParams) => {
            if (!routeParams.step || routeParams.step === "link") {
                return this.patientConsentManagerRouteToRoute(await this.getFirstRoute());
            }
            else {
                return null;
            }
        });
        this.reactiveRedirect(NamedRoutes.GUIDED_PATIENT_CONSENT_ENROLLMENT, async (routeParams) => {
            if (!routeParams.step || routeParams.step === "link") {
                return this.patientConsentManagerRouteToRoute(await this.getFirstRoute());
            }
            else {
                return null;
            }
        });
        this.reactiveRedirect(NamedRoutes.PREREAD_PATIENT_CONSENT_ENROLLMENT, async (routeParams) => {
            if (!routeParams.step) {
                return this.patientConsentManagerRouteToRoute(await this.getFirstRoute());
            }
            else {
                return null;
            }
        });
        this.reactiveRedirect(NamedRoutes.PATIENT_CONSENT_DATA_UPDATE, async (routeParams) => {
            if (!routeParams.step || routeParams.step === "link") {
                return this.patientConsentManagerRouteToRoute(await this.getFirstRoute());
            }
            else {
                return null;
            }
        });
        this.reactiveRedirect(NamedRoutes.PATIENT_IAS_CONSENT, async (routeParams) => {
            if (!routeParams.step || routeParams.step === "link") {
                return this.patientConsentManagerRouteToRoute(await this.getFirstRoute());
            }
            else {
                return null;
            }
        });
        this.reactiveRedirect(NamedRoutes.PATIENT_IAS_DATA_UPDATE, async (routeParams) => {
            if (!routeParams.step || routeParams.step === "link") {
                return this.patientConsentManagerRouteToRoute(await this.getFirstRoute());
            }
            else {
                return null;
            }
        });
    }
    get session() {
        return this._session;
    }
    get studyVersionId() {
        var _a;
        return this.isScreenshotMode ? (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.studyVersionId : this._session._studyVersionId;
    }
    get isDataUpdate() {
        var _a, _b, _c;
        if (((_a = this.previewMetadata) === null || _a === void 0 ? void 0 : _a.requestType) === PreviewRequestType.HEALTH_RECORD_UPDATE) {
            return true;
        }
        if (!!((_b = this.routingUIManager.currentRoute) === null || _b === void 0 ? void 0 : _b.routeName.startsWith(NamedRoutes.PATIENT_CONSENT_DATA_UPDATE.routeName)) ||
            !!((_c = this.routingUIManager.currentRoute) === null || _c === void 0 ? void 0 : _c.routeName.startsWith(NamedRoutes.PATIENT_IAS_DATA_UPDATE.routeName))) {
            return true;
        }
        return false;
    }
    get isIAS() {
        var _a, _b;
        if (this.isScreenshotMode && !this.isScreenshotPreviewMode) {
            return !this._session.studyId;
        }
        if (!!((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName.startsWith(NamedRoutes.PATIENT_IAS_CONSENT.routeName)) ||
            !!((_b = this.routingUIManager.currentRoute) === null || _b === void 0 ? void 0 : _b.routeName.startsWith(NamedRoutes.PATIENT_IAS_DATA_UPDATE.routeName))) {
            return true;
        }
        return false;
    }
    updateConsentFlowPageData(data) {
        var _a, _b, _c, _d, _e, _f;
        this._session._consentFlowPageData = data;
        this._session.additionalParentGuardianExemptSignature = (_a = data === null || data === void 0 ? void 0 : data.additionalParentGuardianExemptSignature) !== null && _a !== void 0 ? _a : null;
        this._session.minorParticipantExemptFromAssent = (_b = data === null || data === void 0 ? void 0 : data.minorParticipantExemptFromAssent) !== null && _b !== void 0 ? _b : null;
        this._session.larParticipantExemptSignature = (_c = data === null || data === void 0 ? void 0 : data.larParticipantExemptSignature) !== null && _c !== void 0 ? _c : null;
        this._session.witnessSkipSignature = (_d = data === null || data === void 0 ? void 0 : data.witnessSkipSignature) !== null && _d !== void 0 ? _d : null;
        this._session.hasLoggedInDuringConsent = (_e = data === null || data === void 0 ? void 0 : data.loggedInDuringConsent) !== null && _e !== void 0 ? _e : null;
        this._session.documentRetrievalLocations = (_f = data === null || data === void 0 ? void 0 : data.documentRetrievalLocations) !== null && _f !== void 0 ? _f : [];
    }
    get recordsSummary() {
        var _a, _b;
        return (_b = (_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.patientData) !== null && _b !== void 0 ? _b : null;
    }
    createPreviewMockedDocuments(count, organizationName) {
        return Array.from({ length: count }, (_, index) => ({
            id: `mock-doc-${index + 1}`,
            filename: `document_${index + 1}.pdf`,
            source: organizationName,
            status: DocumentRetrievalTaskStatus.COMPLETE,
        }));
    }
    createPreviewMockedLocation(organizationName, status, documentCount) {
        return {
            key: crypto.randomUUID(),
            name: organizationName,
            logoUrl: null,
            documents: this.createPreviewMockedDocuments(documentCount, organizationName),
            status,
            authType: null,
        };
    }
    createPreviewMockedLocationData() {
        return [
            this.createPreviewMockedLocation("Riverside General Hospital", DocumentRetrievalLocationStatus.COMPLETE, 3),
            this.createPreviewMockedLocation("Pine Valley Medical Center", DocumentRetrievalLocationStatus.IN_PROGRESS, 1),
            this.createPreviewMockedLocation("Sunset Family Practice", DocumentRetrievalLocationStatus.NEEDS_AUTH, 0),
            this.createPreviewMockedLocation("Metropolitan Orthopedic Associates", DocumentRetrievalLocationStatus.SKIPPED, 0),
        ];
    }
    get isPatientLoggedIn() {
        if (this.isInteractivePreview) {
            return false;
        }
        if (this.isScreenshotPreviewMode) {
            return true;
        }
        return this.authManager.isLoggedIn;
    }
    get consentFlowPageData() {
        return this._session._consentFlowPageData;
    }
    get studyType() {
        var _a, _b;
        if (this._session.study === NOT_LOADED)
            return null;
        return (_b = (_a = this._session.study) === null || _a === void 0 ? void 0 : _a.studyType) !== null && _b !== void 0 ? _b : null;
    }
    determineGuidedConsentCloseRoute() {
        if (this.currentPage !== null)
            return;
        if (!findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [
            NamedRoutes.PATIENTS_SEARCH,
            NamedRoutes.PATIENTS_STUDY_DETAILS,
        ])) {
            return;
        }
        if (this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION &&
            this.authManager.permissions.includes(UserPermissions.PROVIDER)) {
            this._session.guidedConsentCloseRoute = this.routingUIManager.currentRoute;
        }
    }
    get isInteractivePreview() {
        if (!this.authManager.isLoggedIn)
            return false;
        switch (this.authManager.userType) {
            case UserType.PATIENT:
            case UserType.SCREENSHOT_USER:
                return false;
            case UserType.HEALTHCARE_ORGANIZATION:
                return !(this._session.guidedConsentPatientMRN && this.authManager.permissions.includes(UserPermissions.PROVIDER));
            default:
                return true;
        }
    }
    get isScreenshotMode() {
        return this.authManager.userType === UserType.SCREENSHOT_USER;
    }
    get isScreenshotPreviewMode() {
        var _a;
        return this.isScreenshotMode && !!((_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.isPreview);
    }
    get isPopup() {
        return !!this._session.routeBeforePopup;
    }
    get previewMetadata() {
        var _a, _b;
        if (this.isInteractivePreview)
            return this._session.interactivePreviewMetadata;
        return (_b = (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.previewMetadata) !== null && _b !== void 0 ? _b : null;
    }
    get isFirstRoute() {
        if (!this.patientConsentFlow || this.pages === NOT_LOADED)
            return false;
        return this.currentPatientConsentRoute === this.pages[0];
    }
    get currentPatientConsentRoute() {
        if (this.isInteractivePreview) {
            return this._session.currentInteractivePreviewRoute;
        }
        else {
            return this.routeToPatientConsentRoute(this.routingUIManager.currentRoute, this.routingUIManager.currentRouteParams);
        }
    }
    get patientConsentFlow() {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        let patientConsentFlow = null;
        if (this.isInteractivePreview || this.isScreenshotPreviewMode) {
            patientConsentFlow = this.patientConsentFlowFromPreviewConsentFlow((_b = (_a = this.previewMetadata) === null || _a === void 0 ? void 0 : _a.consentFlow) !== null && _b !== void 0 ? _b : null);
        }
        else if (!this.routingUIManager.currentRoute) {
            patientConsentFlow = null;
        }
        else if (((_c = this.routingUIManager.currentRoute) === null || _c === void 0 ? void 0 : _c.routeName.startsWith(NamedRoutes.PATIENT_CONSENT_ENROLLMENT.routeName)) ||
            ((_d = this.routingUIManager.currentRoute) === null || _d === void 0 ? void 0 : _d.routeName.startsWith(NamedRoutes.PATIENT_CONSENT_DATA_UPDATE.routeName)) ||
            ((_e = this.routingUIManager.currentRoute) === null || _e === void 0 ? void 0 : _e.routeName.startsWith(NamedRoutes.PATIENT_IAS_CONSENT.routeName)) ||
            ((_f = this.routingUIManager.currentRoute) === null || _f === void 0 ? void 0 : _f.routeName.startsWith(NamedRoutes.PATIENT_IAS_DATA_UPDATE.routeName)) ||
            ((_g = this.routingUIManager.currentRoute) === null || _g === void 0 ? void 0 : _g.routeName.startsWith(NamedRoutes.GUIDED_PATIENT_CONSENT_ENROLLMENT.routeName)) ||
            ((_h = this.routingUIManager.currentRoute) === null || _h === void 0 ? void 0 : _h.routeName.startsWith(NamedRoutes.PREREAD_PATIENT_CONSENT_ENROLLMENT.routeName))) {
            patientConsentFlow = PatientConsentFlow.ENROLLMENT;
        }
        return patientConsentFlow;
    }
    get patientName() {
        var _a;
        if (!this.isInteractivePreview && !!((_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.name)) {
            return this.consentingPatientInfo.name;
        }
        else if (this.isInteractivePreview || this.isScreenshotMode) {
            return "Sample Patient";
        }
        else {
            return "Unknown Patient Name";
        }
    }
    get isParentGuardian() {
        return this._session.parentGuardianProxyAccess !== null;
    }
    get parentGuardianName() {
        var _a;
        if (this.isInteractivePreview)
            return "Sample Parent/Guardian";
        const name = ((_a = this._session.parentGuardianProxyAccess) === null || _a === void 0 ? void 0 : _a.relatedPerson)
            ? getFullName(this._session.parentGuardianProxyAccess.relatedPerson)
            : null;
        return name !== null && name !== void 0 ? name : "Unknown Parent/Guardian";
    }
    get additionalParentGuardianName() {
        var _a;
        if (this.isInteractivePreview)
            return "Sample Additional Parent/Guardian";
        const name = ((_a = this._session.additionalParentGuardianProxyAccess) === null || _a === void 0 ? void 0 : _a.relatedPerson)
            ? getFullName(this._session.additionalParentGuardianProxyAccess.relatedPerson)
            : null;
        return name !== null && name !== void 0 ? name : "Unknown Additional Parent/Guardian";
    }
    get patientMRN() {
        var _a, _b, _c, _d;
        if (this.isInteractivePreview)
            return "Sample MRN";
        if (this.isScreenshotMode)
            return (_b = (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.patientMRN) !== null && _b !== void 0 ? _b : null;
        if (((_c = this.consentingPatientInfo) === null || _c === void 0 ? void 0 : _c.orgType) === UserType.HEALTHCARE_ORGANIZATION && ((_d = this.consentingPatientInfo) === null || _d === void 0 ? void 0 : _d.mrn)) {
            return this.consentingPatientInfo.mrn;
        }
        else {
            return null;
        }
    }
    get providerName() {
        var _a;
        if (this.isInteractivePreview)
            return "Sample Provider";
        return (_a = this.authManager.name) !== null && _a !== void 0 ? _a : "Unknown Provider Name";
    }
    get patientFriendlyTitle() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return "Unknown Study Title";
        }
        return (getTranslatedStudyText(this._session.study, "patientFriendlyTitle", this.browserLanguageUIManager.currentLanguage) || this._session.study.title);
    }
    get studySectionImages() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return [];
        }
        return this._session.study.studySectionImages;
    }
    get patientPortalName() {
        var _a;
        return (_a = this.authManager.orgPatientPortalName) !== null && _a !== void 0 ? _a : null;
    }
    get studyInfo() {
        if (this.isIAS)
            return null;
        if (this._session.study !== NOT_LOADED && this._session.study !== null)
            return this._session.study;
        if (this._session.studyPublicInfo !== NOT_LOADED && this._session.studyPublicInfo !== null)
            return this._session.studyPublicInfo;
        return NOT_LOADED;
    }
    get studySponsor() {
        var _a, _b;
        if (this.studyInfo === NOT_LOADED) {
            return "Unknown Study Sponsor";
        }
        return (_b = (_a = this.studyInfo) === null || _a === void 0 ? void 0 : _a.sponsorOrganizationName) !== null && _b !== void 0 ? _b : "Unknown Study Sponsor";
    }
    get studyPrimaryContactName() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return null;
        }
        return this._session.study.sitePrimaryContactName;
    }
    get studyPrimaryContactTitle() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return null;
        }
        return this._session.study.sitePrimaryContactTitle;
    }
    get primaryDiseaseOrConditions() {
        var _a, _b;
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return [];
        }
        const diseases = getTranslatedStudyText(this._session.study, "informedConsentPrimaryDiseaseOrConditions", this.browserLanguageUIManager.currentLanguage);
        if (diseases.length > 0) {
            return diseases.filter((item) => item.length > 0);
        }
        return (_b = (_a = this._session.study.primaryDiseaseOrConditions) === null || _a === void 0 ? void 0 : _a.filter((item) => item.length > 0)) !== null && _b !== void 0 ? _b : [];
    }
    get patientFriendlySummary() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return "";
        }
        return (getTranslatedStudyText(this._session.study, "patientFriendlySummary", this.browserLanguageUIManager.currentLanguage) || "Not specified");
    }
    get irbApprovalStamp() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return "";
        }
        return (getTranslatedStudyText(this._session.study, "informedConsentIRBApprovalStamp", this.browserLanguageUIManager.currentLanguage) || "");
    }
    get primaryContactEmail() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return "";
        }
        return this._session.study.sitePrimaryContactEmail || "Not specified";
    }
    get primaryContactPhone() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return null;
        }
        return this._session.study.sitePrimaryContactPhone;
    }
    get risksOfEnrollment() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return "";
        }
        return (getTranslatedStudyText(this._session.study, "risksOfEnrollment", this.browserLanguageUIManager.currentLanguage) ||
            "No risks specified.");
    }
    get benefitsOfEnrollment() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return "";
        }
        return (getTranslatedStudyText(this._session.study, "benefitsOfEnrollment", this.browserLanguageUIManager.currentLanguage) || "No benefits specified.");
    }
    get alternativesToParticipation() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return null;
        }
        return (getTranslatedStudyText(this._session.study, "alternativesToParticipation", this.browserLanguageUIManager.currentLanguage) || null);
    }
    get resourceScopes() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return [];
        }
        return this._session.study.consentDataResourceScopes;
    }
    get sensitivityScopes() {
        if (this._session.study === NOT_LOADED || this._session.study === null || this.patientConsentFlow === null) {
            return [];
        }
        let consentType;
        if (this.patientConsentFlow === PatientConsentFlow.ENROLLMENT) {
            consentType = getDataConsentTypeForProject(this._session.study);
        }
        else {
            return willNeverHappen(this.patientConsentFlow);
        }
        return this._session.study.consentDataSensitivityScopes.filter((scope) => scope !== ConsentDataSensitivityScope.NORMAL && isConsentDataSensitivityScopeValidForConsent(consentType, scope));
    }
    get isFullRecordMode() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return false;
        }
        return this._session.study.fullRecordWithoutRestrictedData === true;
    }
    get consentExpirationDate() {
        var _a;
        if (this.isIAS) {
            return this.consentExpirationService.getConsentExpiration(null, (_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.state);
        }
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        return this.getConsentExpirationFromStudy(this._session.study);
    }
    get isPDDAGeneralDataUse() {
        if (this.isIAS)
            return true;
        if (this.studyInfo === NOT_LOADED || this.studyInfo === null)
            return false;
        return this.studyInfo.pdda && this.studyInfo.projectType === ProjectType.GENERAL_DATA_USE;
    }
    get showClassicLoginPage() {
        return !this.isPDDAGeneralDataUse && this.currentPage === "ENROLLMENT_LOGIN";
    }
    get rightToBeContacted() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return false;
        }
        return !!this._session.study.rightToBeContactedByResearcher;
    }
    get futureUseOfDataText() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return "";
        }
        const translatedText = getTranslatedStudyText(this._session.study, "futureUseOfDataText", this.browserLanguageUIManager.currentLanguage);
        return translatedText || "No data sharing text specified.";
    }
    get showBillOfRightsPage() {
        var _a;
        if (this.isInteractivePreview || this.isScreenshotMode)
            return true;
        return ((_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.state) === USAStates.CA;
    }
    get allowsIdentifiedFutureDataUse() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return false;
        }
        return doesProjectAllowIdentifiedFutureDataUse(this._session.study);
    }
    get showSensitiveDataConfirmationPage() {
        if (this.isInteractivePreview || this.isScreenshotMode || !this.authManager.orgState)
            return true;
        return this.stateLawsService.requiresSensistiveDataConfirmation(this.authManager.orgState);
    }
    get isPDDAStudy() {
        if (this.studyInfo === NOT_LOADED || this.studyInfo === null)
            return null;
        return this.studyInfo.pdda;
    }
    get pddaHealthRecordsUpdateFrequency() {
        var _a, _b;
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        if (!this._session.study.healthRecordsUpdatesEnabled)
            return null;
        const frequency = (_a = this._session.study.healthRecordsUpdatesEvery) !== null && _a !== void 0 ? _a : null;
        const unit = (_b = this._session.study.healthRecordsUpdatesUnit) !== null && _b !== void 0 ? _b : null;
        if (frequency === null || unit === null)
            return null;
        return { frequency, unit };
    }
    get showTimeForVisitsPage() {
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        return (this._session.study.timelineVisitsEnabled === true &&
            this._session.study.timelineVisits &&
            this._session.study.timelineVisits.filter((item) => item.period.length > 0 && item.description.length > 0).length > 0);
    }
    get showEnrollmentDataSharingPage() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return false;
        }
        const translatedText = getTranslatedStudyText(this._session.study, "futureUseOfDataText", this.browserLanguageUIManager.currentLanguage);
        return this._session.study.futureUseOfDataEnabled === true && !!translatedText;
    }
    get showParticipationTerminationPage() {
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        const translatedText = getTranslatedStudyText(this._session.study, "participationTerminationText", this.browserLanguageUIManager.currentLanguage);
        return this._session.study.participationTerminationEnabled === true && !!translatedText;
    }
    get showLeavingStudyEarlyPage() {
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        const translatedText = getTranslatedStudyText(this._session.study, "leavingStudyEarlyText", this.browserLanguageUIManager.currentLanguage);
        return this._session.study.leavingStudyEarlyEnabled === true && !!translatedText;
    }
    get showCostOfParticipationPage() {
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        const translatedText = getTranslatedStudyText(this._session.study, "costOfParticipationText", this.browserLanguageUIManager.currentLanguage);
        return this._session.study.costOfParticipationEnabled === true && !!translatedText;
    }
    get showSharingResultsPage() {
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        const translatedText = getTranslatedStudyText(this._session.study, "sharingResultsText", this.browserLanguageUIManager.currentLanguage);
        return this._session.study.sharingResultsEnabled === true && !!translatedText;
    }
    get showCompensationPage() {
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        const translatedText = getTranslatedStudyText(this._session.study, "compensationText", this.browserLanguageUIManager.currentLanguage);
        return this._session.study.compensation === true && !!translatedText;
    }
    get excludedReason() {
        var _a;
        if (this.studyInfo === NOT_LOADED || this.studyInfo === null) {
            return null;
        }
        if (this.studyInfo.status === StudyStatus.COMPLETED ||
            this.studyInfo.status === StudyStatus.WITHDRAWN ||
            this.studyInfo.isCompletionDateExpired) {
            return PatientConsentExcludedReason.NOT_RECRUITING;
        }
        if (this.isInteractivePreview || this.isScreenshotMode)
            return null;
        if (this.isMinor && !this.isMinorConsentEnabled) {
            return PatientConsentExcludedReason.UNDERAGE;
        }
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return null;
        }
        if (!((_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.state)) {
            return PatientConsentExcludedReason.NOT_IN_UNITED_STATES;
        }
        if (!this.isProvider &&
            this.browserLanguageUIManager.currentLanguage !== StudyTranslationLanguage.en &&
            !this.studyICFTranslationAvailableLanguages.includes(this.browserLanguageUIManager.currentLanguage)) {
            return PatientConsentExcludedReason.TRANSLATIONS_NOT_AVAILABLE;
        }
        return null;
    }
    get videoTranscode() {
        var _a, _b, _c;
        if (this._session.study === NOT_LOADED)
            return null;
        const currentLanguage = this.currentLanguage;
        if (currentLanguage !== "en") {
            let studyLanguage;
            switch (currentLanguage) {
                case "es":
                    studyLanguage = StudyTranslationLanguage.es;
                    break;
                default:
                    break;
            }
            if (studyLanguage) {
                const languageFields = (_b = (_a = this._session.study) === null || _a === void 0 ? void 0 : _a.studyTranslatableFields) === null || _b === void 0 ? void 0 : _b.find((field) => field.language === studyLanguage);
                if (languageFields) {
                    if (languageFields.enrollmentVideo && typeof languageFields.enrollmentVideo === "object") {
                        return languageFields.enrollmentVideo;
                    }
                }
            }
        }
        return (_c = this._session.study) === null || _c === void 0 ? void 0 : _c.enrollmentTranscode;
    }
    get require21CFRPart11ForInformedConsents() {
        var _a;
        return this._session.study !== NOT_LOADED && !!((_a = this._session.study) === null || _a === void 0 ? void 0 : _a.require21CFRPart11ForInformedConsents);
    }
    get needsReauthorization() {
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return false;
        if (this.authManager.gfidProvider === GFIDProvider.CLEAR)
            return false;
        if (this.authManager.userType !== UserType.PATIENT)
            return false;
        if (this.showMinorConsentPages)
            return false;
        return this.require21CFRPart11ForInformedConsents;
    }
    get isProvider() {
        var _a;
        if (((_a = this.previewMetadata) === null || _a === void 0 ? void 0 : _a.consentMode) === PreviewConsentMode.GUIDED)
            return true;
        if (this.isInteractivePreview || this.isScreenshotPreviewMode)
            return false;
        return (this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION &&
            this.authManager.permissions.includes(UserPermissions.PROVIDER));
    }
    get providerId() {
        var _a;
        if (!this.isProvider)
            return null;
        return (_a = this.authManager.providerId) !== null && _a !== void 0 ? _a : null;
    }
    get isPrereadEnrollment() {
        return !!findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [NamedRoutes.PREREAD_PATIENT_CONSENT_ENROLLMENT]);
    }
    get isProxyAccess() {
        var _a, _b, _c;
        if ((_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.isProxyAccess)
            return true;
        return !!((_b = this.authManager.proxyAccess) === null || _b === void 0 ? void 0 : _b.isEnabled) && !!((_c = this.authManager.proxyAccess) === null || _c === void 0 ? void 0 : _c.relatedPerson);
    }
    get isParentGuardianAuthRequired() {
        return this.require21CFRPart11ForInformedConsents || !this.isProxyAccess;
    }
    get isMinorConsentEnabled() {
        var _a, _b;
        if (!this.isProvider &&
            this.authManager.gfidProvider !== GFIDProvider.SMART_ON_FHIR &&
            !this.isScreenshotPreviewMode &&
            !this.isInteractivePreview)
            return false;
        return (_b = (this._session.study !== NOT_LOADED && ((_a = this._session.study) === null || _a === void 0 ? void 0 : _a.minorParticipantConsentEnabled))) !== null && _b !== void 0 ? _b : false;
    }
    get isMinor() {
        var _a, _b, _c, _d, _e;
        if (this.isScreenshotMode &&
            (!!((_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.parentGuardianSignerName) || !!((_b = this.consentFlowPageData) === null || _b === void 0 ? void 0 : _b.minorParticipantSignerName))) {
            return true;
        }
        else if (this.isInteractivePreview) {
            return ((_c = this._session.interactivePreviewMetadata) === null || _c === void 0 ? void 0 : _c.participantType) === PreviewParticipantType.MINOR;
        }
        else {
            return (_e = (_d = this.consentingPatientInfo) === null || _d === void 0 ? void 0 : _d.isUnderage) !== null && _e !== void 0 ? _e : false;
        }
    }
    get showMinorConsentPages() {
        if (this.isScreenshotPreviewMode && this.isMinorConsentEnabled)
            return true;
        return this.isMinor && this.isMinorConsentEnabled;
    }
    get showParticipantConsentPages() {
        if (this.isScreenshotPreviewMode)
            return true;
        return !this.showMinorConsentPages;
    }
    get isAdditionalParentGuardianEnabled() {
        var _a, _b;
        return (_b = (this._session.study !== NOT_LOADED && ((_a = this._session.study) === null || _a === void 0 ? void 0 : _a.additionalParentGuardianEnabled))) !== null && _b !== void 0 ? _b : false;
    }
    checkIfInterimPage(page) {
        return page.endsWith("_INTERIM_PAGE");
    }
    get isInterimPage() {
        if (!this.currentPage)
            return false;
        return this.checkIfInterimPage(this.currentPage);
    }
    get studyICFTranslationAvailableLanguages() {
        var _a, _b;
        if (this._session.study === NOT_LOADED)
            return [];
        const availableLanguages = [StudyTranslationLanguage.en];
        const otherLanguages = (_b = (_a = this._session.study) === null || _a === void 0 ? void 0 : _a.studyTranslatableFields.filter((field) => field.language !== StudyTranslationLanguage.en && field.enabled && field.completed).map((field) => field.language)) !== null && _b !== void 0 ? _b : [];
        availableLanguages.push(...otherLanguages);
        return availableLanguages;
    }
    get currentLanguage() {
        return this.browserLanguageUIManager.currentLanguage;
    }
    get retrieval() {
        var _a, _b;
        if (this.isScreenshotMode)
            return (_b = (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.retrieval) !== null && _b !== void 0 ? _b : null;
        if (!this._session.retrievalBatchId)
            return null;
        return this.documentRetrievalManager.retrievals.find((retrieval) => retrieval.batchId === this._session.retrievalBatchId);
    }
    get pendingRetrieval() {
        var _a;
        const patientId = (_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id;
        if (!patientId)
            return null;
        return this.documentRetrievalManager.retrievals.find((retrieval) => {
            var _a;
            return retrieval.studyId === this._session.studyId &&
                retrieval.patientId === ((_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id) &&
                retrieval.isDataUpdate === this.isDataUpdate &&
                !retrieval.finalized &&
                !retrieval.documentSelectionComplete;
        });
    }
    get locatingPDDERecords() {
        if (this.isInteractivePreview || this.isScreenshotMode)
            return false;
        if (this.isPDDAGeneralDataUse)
            return true;
        if (this.currentPage !== "ENROLLMENT_PDDA_RECORD_LOCATION")
            return false;
        if (!this.retrieval)
            return false;
        return !this.retrieval.medAlliesGetDocumentsComplete || !this.retrieval.medAlliesGetEndpointsComplete;
    }
    get canAddLocations() {
        if (!!this.retrieval && this.retrieval.allowOnlyTargetedFacilities === true) {
            return false;
        }
        return true;
    }
    get zeroLocationsFound() {
        if (!!this.retrieval && this.retrieval.locations.length === 0) {
            return true;
        }
        return false;
    }
    get hasPatientConsented() {
        var _a, _b;
        if (this.isInteractivePreview || this.isScreenshotMode || this.isScreenshotPreviewMode) {
            return this.isDataUpdate;
        }
        return (_b = (_a = this._session.consentInfo) === null || _a === void 0 ? void 0 : _a.hasPatientConsented) !== null && _b !== void 0 ? _b : false;
    }
    get excludedPagesForReconsent() {
        let excludedPagesForReconsent = [];
        if (this.isPDDAGeneralDataUse) {
            excludedPagesForReconsent = [
                "ENROLLMENT_WELCOME",
                "ENROLLMENT_REQUEST_DETAILS_POPUP",
                "ENROLLMENT_LOGIN",
                "ENROLLMENT_IDENTITY_VERIFIED",
            ];
        }
        return excludedPagesForReconsent;
    }
    get mayHaveAccessibleRecordsOnceReady() {
        if (!this.retrieval)
            return false;
        if (this.retrieval.finalized) {
            return this.retrieval.documents.some((document) => document.status === DocumentRetrievalTaskStatus.COMPLETE);
        }
        return this._session.documentRetrievalLocations.some((location) => location.status === DocumentRetrievalLocationStatus.COMPLETE ||
            location.status === DocumentRetrievalLocationStatus.IN_PROGRESS);
    }
    setInMemoryLanguage(language) {
        this.browserLanguageUIManager.setInMemoryLanguage(language);
    }
    get isRelevantRoute() {
        return !!findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [
            NamedRoutes.PATIENT_CONSENT_ENROLLMENT,
            NamedRoutes.PATIENT_CONSENT_DATA_UPDATE,
            NamedRoutes.PATIENT_IAS_CONSENT,
            NamedRoutes.PATIENT_IAS_DATA_UPDATE,
            NamedRoutes.GUIDED_PATIENT_CONSENT_ENROLLMENT,
            NamedRoutes.PREREAD_PATIENT_CONSENT_ENROLLMENT,
        ]);
    }
    async refreshStudyAndPatientInfo() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        this._session.guidedConsentPatientMRN = (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.mrn) !== null && _b !== void 0 ? _b : null;
        if (this.isInteractivePreview)
            return;
        if (((_c = this.routingUIManager.currentRouteParams) === null || _c === void 0 ? void 0 : _c.studyId) === this.iasPath) {
            this._session.studyId = undefined;
        }
        else {
            this._session.studyId = (_d = this.routingUIManager.currentRouteParams) === null || _d === void 0 ? void 0 : _d.studyId;
        }
        if (this._session.studyId) {
            this._session.studyPublicInfo = await this.studyManager.getStudyPublicInfo(this._session.studyId, this.studyVersionId ? { studyVersionId: this.studyVersionId } : StudyVersionStatus.ACTIVE);
            if (this._session.studyPublicInfo) {
                this._session._studyVersionId = this._session.studyPublicInfo.studyVersionId;
            }
        }
        if (!this.authManager.orgId) {
            this.redirectToExcludedPageIfNecessary();
            return;
        }
        if (((_e = this.routingUIManager.currentRouteParams) === null || _e === void 0 ? void 0 : _e.link) && !this.pathLink && this.currentPage) {
            this.goToPage(this.currentPage);
            return;
        }
        if (!this.isRelevantRoute) {
            this._session.study = NOT_LOADED;
            this._session.studyPublicInfo = NOT_LOADED;
            this._session.guidedConsentPatientSearchResult = NOT_LOADED;
            return;
        }
        if (this._session.guidedConsentPatientMRN) {
            const patient = await this.mrnSearchService.searchByMRNAndGetPatientDetails(this.authManager.orgId, {
                mrn: this._session.guidedConsentPatientMRN,
            });
            this._session.guidedConsentPatientSearchResult = patient;
        }
        if (this._session.studyId) {
            await this.enteredConsentFlow();
            await this.saveConsentFlowProgress();
            const study = await this.studyManager.getStudy(this._session.studyId, this.studyVersionId ? { studyVersionId: this.studyVersionId } : StudyVersionStatus.ACTIVE);
            this._session.study = study;
            if (this.showMinorConsentPages && ((_f = this.authManager.proxyAccess) === null || _f === void 0 ? void 0 : _f.isEnabled) && ((_g = this.authManager.proxyAccess) === null || _g === void 0 ? void 0 : _g.relatedPerson)) {
                this._session.parentGuardianProxyAccess = { ...this.authManager.proxyAccess };
            }
        }
        await this.loadPatientConsent();
        if (this.hasPatientConsented && !this._session.hasConsentBeenSubmittedThisFlow && !this.isDataUpdate) {
            if (this.isIAS || this.isPDDAStudy) {
                this.routingUIManager.redirectTo(this.isIAS ? NamedRoutes.PATIENT_IAS_DATA_UPDATE : NamedRoutes.PATIENT_CONSENT_DATA_UPDATE, (_h = this.routingUIManager.currentRouteParams) !== null && _h !== void 0 ? _h : undefined);
            }
            else {
                this.showConsentedAlreadyBannerIfNeccessary(true);
            }
        }
        else if (!this.hasPatientConsented && this.isDataUpdate) {
            this.routingUIManager.redirectTo(this.isIAS ? NamedRoutes.PATIENT_IAS_CONSENT : NamedRoutes.PATIENT_CONSENT_ENROLLMENT, (_j = this.routingUIManager.currentRouteParams) !== null && _j !== void 0 ? _j : undefined);
        }
        if (this._session.studyId) {
            if (this.redirectToWelcomeBackPageOrContinueGuidedConsentIfNecessary())
                return;
            await this.redirectIfNecessary();
        }
    }
    async enteredConsentFlow() {
        var _a;
        if (this._session.didEnterConsentFlow || !this.authManager.orgId || !((_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id) || !this._session.studyId) {
            return;
        }
        this._session.didEnterConsentFlow = true;
        await this.refreshConsentFlowProgressIfNecessary();
    }
    async refreshConsentFlowProgressIfNecessary() {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (this.isInteractivePreview || this.isScreenshotMode)
            return;
        if (!this.authManager.orgId || !((_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id) || !this._session.studyId) {
            return;
        }
        const consentFlowProgress = await this.patientService.getConsentFlowProgress(this.authManager.orgId, this.consentingPatientInfo.id, {
            studyId: this._session.studyId,
            consentUIFlow: ConsentUIFlow.PATIENT_DIRECTED_DATA_EXCHANGE,
            isDataUpdate: this.isDataUpdate,
        });
        if (consentFlowProgress) {
            this._session._studyVersionId = (_b = consentFlowProgress.studyVersionId) !== null && _b !== void 0 ? _b : undefined;
            this._session.continueWhereILeftOffRoute = consentFlowProgress.savedPage
                ? consentFlowProgress.savedPage
                : null;
            this._session.patientInformedConsentEnabled = consentFlowProgress.consentEnabled;
            this._session.savedConsentedByLink = (_c = consentFlowProgress.consentedByLink) !== null && _c !== void 0 ? _c : false;
            this._session.continueGuidedConsent = (_d = (this.isProvider && consentFlowProgress.continueGuidedConsent)) !== null && _d !== void 0 ? _d : false;
            this._session.isInterested = consentFlowProgress.interested;
            this._session.additionalParentGuardianExemptSignature = (_e = consentFlowProgress.additionalParentGuardianExemptSignature) !== null && _e !== void 0 ? _e : null;
            this._session.minorParticipantExemptFromAssent = (_f = consentFlowProgress.minorParticipantExemptFromAssent) !== null && _f !== void 0 ? _f : null;
            this._session.larParticipantExemptSignature = (_g = consentFlowProgress.larParticipantExemptSignature) !== null && _g !== void 0 ? _g : null;
            this._session.witnessSkipSignature = (_h = consentFlowProgress.witnessSkipSignature) !== null && _h !== void 0 ? _h : null;
        }
    }
    async loadPatientConsent() {
        var _a, _b, _c, _d;
        if (!((_a = this.authManager) === null || _a === void 0 ? void 0 : _a.orgId) || !((_b = this.consentingPatientInfo) === null || _b === void 0 ? void 0 : _b.id)) {
            return;
        }
        try {
            if (this._session.studyId) {
                if (this._session.study === NOT_LOADED || this._session.study === null) {
                    return;
                }
                const consentTypes = getConsentTypesForProject(this._session.study, this.allowsIdentifiedFutureDataUse);
                for (const consentType of consentTypes) {
                    const evaluatedConsent = await this.patientService.hasPatientConsentedToStudy(this.authManager.orgId, this.consentingPatientInfo.id, this._session.studyId, consentType, true);
                    if (consentType ===
                        getPrimaryConsentTypeForProject({ projectType: (_c = this._session.study) === null || _c === void 0 ? void 0 : _c.projectType, pdda: (_d = this._session.study) === null || _d === void 0 ? void 0 : _d.pdda })) {
                        this._session.consentInfo = evaluatedConsent;
                    }
                }
            }
            else {
                try {
                    const consentTypes = getIASConsentTypes();
                    for (const consentType of consentTypes) {
                        const evaluatedConsent = await this.patientService.hasPatientConsentedToIAS(this.authManager.orgId, this.consentingPatientInfo.id, consentType, true);
                        if (consentType === getPrimaryIASConsentType()) {
                            this._session.consentInfo = evaluatedConsent;
                        }
                    }
                }
                catch (e) {
                    console.error("Check patient IAS consent API failed", e);
                }
            }
        }
        catch (e) {
            console.error("Check patient consented API failed", e);
        }
    }
    showConsentedAlreadyBannerIfNeccessary(hasPatientConsentedToStudy) {
        var _a;
        if (!this.currentPage)
            return;
        if (this._session.hasConsentBeenSubmittedThisFlow)
            return;
        const doNotShowConsentedAlreadyBanner = [
            "ENROLLMENT_CONFIRMED",
            "ENROLLMENT_DECLINED",
            "ENROLLMENT_PREREAD_THANKYOU",
            "ENROLLMENT_IM_INTERESTED_THANKYOU",
            "ENROLLMENT_EXCLUDED",
        ].includes(this.currentPage);
        if (!this._session.hasShownConsentedAlreadyBanner && !doNotShowConsentedAlreadyBanner) {
            this._session.hasShownConsentedAlreadyBanner = true;
            if (hasPatientConsentedToStudy) {
                if (this.isProvider) {
                    if ((_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.name) {
                        this.bannerUIManager.displayBanner({
                            text: t("{{ patientName }} has already consented to enrollment for this study.", {
                                patientName: this.consentingPatientInfo.name,
                            }),
                        });
                    }
                }
                else {
                    this.bannerUIManager.displayBanner({
                        text: t("You have already consented to enrollment for this study."),
                    });
                }
            }
        }
    }
    async getFirstRoute() {
        while (this.pages === NOT_LOADED) {
            await when(() => this.pages !== NOT_LOADED);
        }
        return this.pages[0];
    }
    async startInteractivePreview(studyId, studyVersionId, previewMetadata) {
        this.reset();
        this._session.studyId = studyId;
        this._session._studyVersionId = studyVersionId;
        this._session.studyPublicInfo = await this.studyManager.getStudyPublicInfo(studyId, { studyVersionId });
        this._session.study = await this.studyManager.getStudy(this._session.studyId, { studyVersionId });
        this._session.interactivePreviewMetadata = previewMetadata;
        this._session.documentRetrievalLocations = this.previewMockedLocationData;
        this._session.contactMethod = null;
        this._session.contactEmail = null;
        this._session.contactPhone = null;
        if (this.patientConsentFlow === null)
            throw new Error("patientConsentFlow should not be null");
        await i18nSetLanguage(previewMetadata.language);
        this._session.currentInteractivePreviewRoute = await this.getFirstRoute();
    }
    redirectToExcludedPageIfNecessary() {
        if (!this.currentPatientConsentRoute)
            return false;
        if (this.excludedReason !== null) {
            if (!["ENROLLMENT_EXCLUDED"].includes(this.currentPatientConsentRoute)) {
                this.goToPage("ENROLLMENT_EXCLUDED");
            }
            return true;
        }
        return false;
    }
    redirectToWelcomeBackPageOrContinueGuidedConsentIfNecessary() {
        if (!this.currentPatientConsentRoute)
            return false;
        if (this._session.didTryToRedirectToWelcomeBackPage)
            return false;
        this._session.didTryToRedirectToWelcomeBackPage = true;
        if (this.isProvider) {
            if (this.excludedReason !== null)
                return false;
            if (this._session.continueGuidedConsent && this._session.continueWhereILeftOffRoute) {
                this.goToPage(this._session.continueWhereILeftOffRoute);
            }
            return true;
        }
        else if (!this.isPDDAGeneralDataUse &&
            this._session.continueWhereILeftOffRoute &&
            this.excludedReason === null &&
            !["ENROLLMENT_WELCOME_BACK"].includes(this.currentPatientConsentRoute)) {
            this.goToPage("ENROLLMENT_WELCOME_BACK");
            return true;
        }
        return false;
    }
    async redirectIfNecessary() {
        await when(() => this._session.study !== NOT_LOADED);
        if (!this._session.study)
            return;
        if (this.pages === NOT_LOADED)
            return;
        if (this.isScreenshotMode)
            return;
        const currentRoute = this.routingUIManager.currentRoute;
        if (!findMatchingNamedRouteParent(currentRoute, [
            NamedRoutes.PATIENT_CONSENT_ENROLLMENT,
            NamedRoutes.PATIENT_CONSENT_DATA_UPDATE,
            NamedRoutes.PATIENT_IAS_CONSENT,
            NamedRoutes.PATIENT_IAS_DATA_UPDATE,
            NamedRoutes.GUIDED_PATIENT_CONSENT_ENROLLMENT,
            NamedRoutes.PREREAD_PATIENT_CONSENT_ENROLLMENT,
        ])) {
            return;
        }
        if (this.redirectToExcludedPageIfNecessary()) {
            return;
        }
        const currentRouteName = this.currentPatientConsentRoute;
        const routePrefix = "ENROLLMENT";
        if (currentRouteName === `${routePrefix}_BILL_OF_RIGHTS` && !this.showBillOfRightsPage) {
            this.goToPage(`${routePrefix}_WELCOME`);
        }
        else if (currentRouteName === "ENROLLMENT_DATA_SHARING" && !this.showEnrollmentDataSharingPage) {
            this.goToPage("ENROLLMENT_CHOICE");
        }
        else if (currentRouteName === `${routePrefix}_SENSITIVE_DATA_CONFIRMATION` && !this.showSensitiveDataConfirmationPage) {
            this.goToPage("ENROLLMENT_DATA_SHARING");
        }
        else if (currentRouteName === "ENROLLMENT_REAUTHORIZATION" &&
            (!this._session.patientInformedConsentEnabled || this.showMinorConsentPages)) {
            this.goToPage("ENROLLMENT_INFORMED_CONSENT");
        }
        else if (currentRouteName === "ENROLLMENT_HIPAA_AUTHORIZATION" && !this._session.patientInformedConsentEnabled) {
            this.goToPage("ENROLLMENT_INFORMED_CONSENT");
        }
        else if (currentRouteName === "ENROLLMENT_PDDA_RECORD_LOCATION" && !this.isPDDAStudy) {
            this.goToPage("ENROLLMENT_WELCOME");
        }
        else if (currentRouteName === `${routePrefix}_PARTICIPATION_SIGNATURE` &&
            !this.isPDDAGeneralDataUse &&
            (!this._session.patientInformedConsentEnabled || this.showMinorConsentPages)) {
            this.goToPage(`${routePrefix}_INFORMED_CONSENT`);
        }
        else if (currentRouteName === `${routePrefix}_PASS_DEVICE_TO_PARENT_GUARDIAN` &&
            (!this._session.patientInformedConsentEnabled ||
                !this.showMinorConsentPages ||
                this.isProvider ||
                !this.isParentGuardianAuthRequired ||
                this.isProxyAccess)) {
            this.goToPage(`${routePrefix}_INFORMED_CONSENT`);
        }
        else if (currentRouteName === `${routePrefix}_PARENT_GUARDIAN_REAUTHORIZATION` &&
            (!this._session.patientInformedConsentEnabled ||
                !this.showMinorConsentPages ||
                this.isProvider ||
                !this.isParentGuardianAuthRequired)) {
            this.goToPage(`${routePrefix}_INFORMED_CONSENT`);
        }
        else if (currentRouteName === `${routePrefix}_PARENT_GUARDIAN_SIGNATURE` &&
            (!this._session.patientInformedConsentEnabled ||
                !this.showMinorConsentPages ||
                (!this.isProvider && !this._session.parentGuardianProxyAccess))) {
            this.goToPage(`${routePrefix}_INFORMED_CONSENT`);
        }
        else if (currentRouteName === `${routePrefix}_PASS_DEVICE_TO_ADDITIONAL_PARENT_GUARDIAN` &&
            (!this._session.patientInformedConsentEnabled ||
                !this.showMinorConsentPages ||
                !this.isAdditionalParentGuardianEnabled ||
                !this._session.parentGuardianSignature)) {
            this.goToPage(`${routePrefix}_INFORMED_CONSENT`);
        }
        else if (currentRouteName === `${routePrefix}_ADDITIONAL_PARENT_GUARDIAN_REAUTHORIZATION` &&
            (!this._session.patientInformedConsentEnabled ||
                !this.showMinorConsentPages ||
                this.isProvider ||
                !this.isAdditionalParentGuardianEnabled ||
                !!this._session.additionalParentGuardianExemptSignature ||
                !this._session.parentGuardianSignature)) {
            this.goToPage(`${routePrefix}_INFORMED_CONSENT`);
        }
        else if (currentRouteName === `${routePrefix}_ADDITIONAL_PARENT_GUARDIAN_SIGNATURE` &&
            (!this._session.patientInformedConsentEnabled ||
                !this.showMinorConsentPages ||
                !this.isAdditionalParentGuardianEnabled ||
                (!this.isProvider && !this._session.additionalParentGuardianProxyAccess) ||
                !!this._session.additionalParentGuardianExemptSignature ||
                !this._session.parentGuardianSignature)) {
            this.goToPage(`${routePrefix}_INFORMED_CONSENT`);
        }
        else if (currentRouteName === `${routePrefix}_PASS_DEVICE_TO_MINOR` &&
            (!this._session.patientInformedConsentEnabled ||
                !this.showMinorConsentPages ||
                !this._session.parentGuardianSignature ||
                (this.isAdditionalParentGuardianEnabled &&
                    !this._session.additionalParentGuardianExemptSignature &&
                    !this._session.additionalParentGuardianSignature))) {
            this.goToPage(`${routePrefix}_INFORMED_CONSENT`);
        }
        else if (currentRouteName === `${routePrefix}_MINOR_SIGNATURE` &&
            (!this._session.patientInformedConsentEnabled ||
                !this.showMinorConsentPages ||
                !!this._session.minorParticipantExemptFromAssent ||
                !this._session.parentGuardianSignature ||
                (this.isAdditionalParentGuardianEnabled &&
                    !this._session.additionalParentGuardianExemptSignature &&
                    !this._session.additionalParentGuardianSignature))) {
            this.goToPage(`${routePrefix}_INFORMED_CONSENT`);
        }
        else if (currentRouteName === `${routePrefix}_PASS_DEVICE_TO_WITNESS` &&
            (!this.isProvider || !this._session.patientInformedConsentEnabled)) {
            this.goToPage(`${routePrefix}_INFORMED_CONSENT`);
        }
        else if (currentRouteName === `${routePrefix}_WITNESS_SIGNATURE` &&
            (!this.isProvider || !this._session.patientInformedConsentEnabled || this._session.witnessSkipSignature)) {
            this.goToPage(`${routePrefix}_INFORMED_CONSENT`);
        }
        else if (currentRouteName === `${routePrefix}_PASS_DEVICE_TO_PROVIDER` &&
            (!this.isProvider || !this._session.patientInformedConsentEnabled)) {
            this.goToPage(`${routePrefix}_INFORMED_CONSENT`);
        }
        else if (currentRouteName === `${routePrefix}_PROVIDER_AUTHORIZATION` &&
            ((this.isProvider && !this.showMinorConsentPages && !this._session.patientSignatureHIPAA) ||
                !this.isProvider ||
                !this._session.patientInformedConsentEnabled)) {
            this.goToPage(`${routePrefix}_INFORMED_CONSENT`);
        }
        else if (currentRouteName === "ENROLLMENT_CONFIRMED" &&
            this._session.hasAuthorizedConsentEnrollment !== true &&
            !this.isProvider &&
            !this.isDataUpdate) {
            this.goToPage(`ENROLLMENT_INFORMED_CONSENT`);
        }
        else if (currentRouteName === "ENROLLMENT_DECLINED" && this._session.hasAuthorizedConsentEnrollment !== false) {
            this.goToPage(`ENROLLMENT_INFORMED_CONSENT`);
        }
        else if (currentRouteName === `${routePrefix}_EXCLUDED` && this.excludedReason === null) {
            this.goToPage(`${routePrefix}_WELCOME`);
        }
        else if (!this.isScreenshotPreviewMode && this.isInterimPage) {
            this.goToPage(`${routePrefix}_WELCOME`);
        }
        else if (this.currentPage) {
            if (currentRouteName !== "ENROLLMENT_WELCOME_BACK" &&
                !this.isPopup &&
                this.enrollmentPages &&
                this.enrollmentPages !== NOT_LOADED &&
                !this.enrollmentPages.includes(this.currentPage)) {
                this.goToPage("ENROLLMENT_WELCOME");
            }
        }
    }
    get currentPage() {
        var _a;
        if (this.isInteractivePreview)
            return this._session.currentInteractivePreviewRoute;
        if (this.patientConsentFlow === null)
            return null;
        const rawStep = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.step;
        if (!rawStep)
            return null;
        const step = patientConsentRouteFromParts({
            flow: this.patientConsentFlow,
            step: unslugify(rawStep),
        });
        if (!step)
            return null;
        return step;
    }
    get pathLink() {
        var _a, _b, _c;
        if (this.isInteractivePreview)
            return null;
        if (this.patientConsentFlow === null || !this._session.studyId)
            return null;
        const rawLink = (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.link) !== null && _b !== void 0 ? _b : (_c = this.routingUIManager.currentRouteParams) === null || _c === void 0 ? void 0 : _c.step;
        if (!rawLink)
            return null;
        const link = unslugify(rawLink);
        if (!Object.values(PatientConsentLink).includes(link))
            return null;
        if (!link)
            return null;
        return link;
    }
    get consentedByLink() {
        return this.pathLink === PatientConsentLink.LINK || this._session.savedConsentedByLink;
    }
    get currentPageIndex() {
        if (this.currentPage === null || this.pages === NOT_LOADED)
            return null;
        return this.pages.indexOf(this.currentPage);
    }
    get hideStudyInformationStep() {
        if (!this.authManager.orgId)
            return false;
        return this.environment.HIDE_STUDY_INFORMATION_STEP_ORG_IDS.split(/\s*,\s*/).includes(this.authManager.orgId);
    }
    get enrollmentPages() {
        if (this.isIAS) {
            return this.pddaGeneralDataUseEnrollmentPages;
        }
        if (this.studyInfo === NOT_LOADED)
            return NOT_LOADED;
        if (this.isPDDAGeneralDataUse) {
            return this.pddaGeneralDataUseEnrollmentPages;
        }
        if (this.patientConsentFlow !== PatientConsentFlow.ENROLLMENT)
            return null;
        const pages = [];
        if (!this.isPatientLoggedIn) {
            pages.push("ENROLLMENT_LOGIN");
            if (this._session.study === NOT_LOADED)
                return pages;
        }
        if (this._session.study === NOT_LOADED)
            return NOT_LOADED;
        if (this._session.study === null)
            return null;
        pages.push("ENROLLMENT_WELCOME");
        const isInterventionalStudy = this.studyType === StudyType.INTERVENTIONAL;
        if (this.showBillOfRightsPage)
            pages.push("ENROLLMENT_BILL_OF_RIGHTS");
        if (!this.hideStudyInformationStep)
            pages.push("ENROLLMENT_STUDY_INFORMATION");
        if (this._session.study.purposeOfResearchEnabled)
            pages.push("ENROLLMENT_PURPOSE_OF_RESEARCH");
        if (isInterventionalStudy || (!isInterventionalStudy && !!this._session.study.lengthOfStudyEnabled)) {
            pages.push("ENROLLMENT_LENGTH_OF_RESEARCH");
        }
        if (this.isPDDAStudy && this.pddaHealthRecordsUpdateFrequency !== null) {
            pages.push("ENROLLMENT_HEALTH_RECORD_UPDATES");
        }
        if (isInterventionalStudy) {
            pages.push("ENROLLMENT_PROCEDURES_AND_INTERVENTIONS");
        }
        else if (!isInterventionalStudy && !!this._session.study.interventionsEnabled) {
            pages.push("ENROLLMENT_PARTICIPATION_AND_ACTIVITIES");
        }
        if (this.showTimeForVisitsPage)
            pages.push("ENROLLMENT_TIMELINE_OF_VISITS");
        pages.push("ENROLLMENT_RISKS", "ENROLLMENT_BENEFITS");
        pages.push("ENROLLMENT_CHOICE");
        if (this.showLeavingStudyEarlyPage)
            pages.push("ENROLLMENT_LEAVING_STUDY_EARLY");
        if (isInterventionalStudy || (!isInterventionalStudy && !!this._session.study.injuryProtocolEnabled)) {
            pages.push("ENROLLMENT_INJURY_PROTOCOL");
        }
        if (this.showParticipationTerminationPage)
            pages.push("ENROLLMENT_PARTICIPATION_TERMINATION");
        pages.push("ENROLLMENT_DATA_TYPES");
        if (this.showEnrollmentDataSharingPage)
            pages.push("ENROLLMENT_DATA_SHARING");
        if (isInterventionalStudy || (!isInterventionalStudy && !!this._session.study.confidentialityMeasuresEnabled)) {
            pages.push("ENROLLMENT_CONFIDENTIALITY_OF_DATA");
        }
        if (this.showCompensationPage)
            pages.push("ENROLLMENT_PATIENT_COMPENSATION");
        if (isInterventionalStudy) {
            if (this.showCostOfParticipationPage)
                pages.push("ENROLLMENT_COST_OF_PARTICIPATION");
            if (this.showSharingResultsPage)
                pages.push("ENROLLMENT_SHARING_RESULTS");
            pages.push("ENROLLMENT_CONTACT");
        }
        if (this._session.study.additionalSections) {
            const currentLanguage = this.currentLanguage;
            let studyLanguage = StudyTranslationLanguage.en;
            switch (currentLanguage) {
                case "es":
                    studyLanguage = StudyTranslationLanguage.es;
                    break;
                case "en":
                default:
                    studyLanguage = StudyTranslationLanguage.en;
                    break;
            }
            this._session.study.additionalSections
                .filter((additionalSection) => !!additionalSection.included &&
                additionalSection.title.trim().length &&
                additionalSection.description.trim().length &&
                additionalSection.language === studyLanguage)
                .slice(0, maxStudyAdditionalSections)
                .forEach((_, index) => pages.push(`ENROLLMENT_CUSTOM_SECTION_${index + 1}`));
        }
        if (!this.isPrereadEnrollment && !this.isScreenshotPreviewMode)
            pages.push("ENROLLMENT_INFORMED_CONSENT");
        if ((!!this._session.patientInformedConsentEnabled && !this.isPrereadEnrollment && !this._session.isInterested) ||
            this.isScreenshotPreviewMode) {
            if (this.needsReauthorization)
                pages.push("ENROLLMENT_REAUTHORIZATION");
            pages.push("ENROLLMENT_HIPAA_AUTHORIZATION");
            if (this.showParticipantConsentPages) {
                if (this.isScreenshotPreviewMode) {
                    pages.push("ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_IN_PARTICIPANT_CONSENT_INTERIM_PAGE", "ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_IN");
                }
                pages.push("ENROLLMENT_PARTICIPATION_SIGNATURE");
            }
            if (this.showMinorConsentPages) {
                if (this.isScreenshotPreviewMode) {
                    pages.push("ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_IN_MINOR_CONSENT_INTERIM_PAGE", "ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_IN");
                }
                if (!this.isProvider && this.isParentGuardianAuthRequired) {
                    if (!this.isProxyAccess)
                        pages.push("ENROLLMENT_PASS_DEVICE_TO_PARENT_GUARDIAN");
                    pages.push("ENROLLMENT_PARENT_GUARDIAN_REAUTHORIZATION");
                }
                pages.push("ENROLLMENT_PARENT_GUARDIAN_SIGNATURE");
                if (this.isAdditionalParentGuardianEnabled) {
                    pages.push("ENROLLMENT_PASS_DEVICE_TO_ADDITIONAL_PARENT_GUARDIAN");
                    if (!this._session.additionalParentGuardianExemptSignature) {
                        if (!this.isProvider)
                            pages.push("ENROLLMENT_ADDITIONAL_PARENT_GUARDIAN_REAUTHORIZATION");
                        pages.push("ENROLLMENT_ADDITIONAL_PARENT_GUARDIAN_SIGNATURE");
                    }
                }
                pages.push("ENROLLMENT_PASS_DEVICE_TO_MINOR");
                if (!this._session.minorParticipantExemptFromAssent)
                    pages.push("ENROLLMENT_MINOR_SIGNATURE");
            }
            if (this.isProvider) {
                pages.push("ENROLLMENT_PASS_DEVICE_TO_WITNESS");
                if (!this._session.witnessSkipSignature)
                    pages.push("ENROLLMENT_WITNESS_SIGNATURE");
                pages.push("ENROLLMENT_PASS_DEVICE_TO_PROVIDER");
                pages.push("ENROLLMENT_PROVIDER_AUTHORIZATION");
            }
            if (this.isPDDAStudy) {
                pages.push("ENROLLMENT_PDDA_RECORD_LOCATION");
            }
            pages.push("ENROLLMENT_CONFIRMED");
        }
        if ((!this._session.patientInformedConsentEnabled && !this._session.isInterested && !this.isPrereadEnrollment) ||
            this.isScreenshotPreviewMode) {
            if (this.isScreenshotPreviewMode) {
                pages.push("ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_OUT_INTERIM_PAGE", "ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_OUT");
            }
            pages.push("ENROLLMENT_DECLINED");
        }
        if ((!!this._session.isInterested && !this.isPrereadEnrollment) ||
            (this.isScreenshotPreviewMode && !!this._session.study.informedConsentInterested)) {
            if (this.isScreenshotPreviewMode) {
                pages.push("ENROLLMENT_INFORMED_CONSENT_PREVIEW_IM_INTERESTED_INTERIM_PAGE", "ENROLLMENT_INFORMED_CONSENT_PREVIEW_IM_INTERESTED");
            }
            pages.push("ENROLLMENT_IM_INTERESTED_THANKYOU");
        }
        if (this.isPrereadEnrollment) {
            pages.push("ENROLLMENT_PREREAD_THANKYOU");
        }
        return pages;
    }
    get pddaGeneralDataUseEnrollmentPages() {
        var _a, _b;
        if (!this.isIAS && (this.studyInfo === NOT_LOADED || this.studyInfo === null))
            return null;
        if (this.patientConsentFlow !== PatientConsentFlow.ENROLLMENT)
            return null;
        const pages = [];
        pages.push("ENROLLMENT_WELCOME");
        if (!this.isInteractivePreview) {
            pages.push("ENROLLMENT_REQUEST_DETAILS_POPUP");
        }
        pages.push("ENROLLMENT_LOGIN");
        if (!this.hasPatientConsented && ((_b = (_a = this._session.consentInfo) === null || _a === void 0 ? void 0 : _a.consentRecord) === null || _b === void 0 ? void 0 : _b.consentStatus) === ConsentStatus.OPTED_OUT) {
            pages.push("ENROLLMENT_DECLINED_ACCESS");
        }
        pages.push("ENROLLMENT_IDENTITY_VERIFIED");
        if (!this.isDataUpdate) {
            pages.push("ENROLLMENT_PARTICIPATION_SIGNATURE");
        }
        if (this.isScreenshotPreviewMode && !this.isDataUpdate) {
            pages.push("ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_IN_PARTICIPANT_CONSENT_INTERIM_PAGE");
        }
        if (this.isDataUpdate || !!this._session.patientInformedConsentEnabled || this.isScreenshotPreviewMode) {
            pages.push("ENROLLMENT_PDDA_RECORD_LOCATION");
            if (this.isScreenshotMode && this._session.documentRetrievalLocations.length > 0) {
                pages.push("ENROLLMENT_PDDA_RECORD_LOCATION_SCREENSHOT_REVIEW_AND_SHARE");
            }
            if (!(this.isScreenshotMode && this.isIAS)) {
                pages.push("ENROLLMENT_CONFIRMED");
            }
        }
        if (!this.isDataUpdate && (!this._session.patientInformedConsentEnabled || this.isScreenshotPreviewMode)) {
            if (this.isScreenshotPreviewMode) {
                pages.push("ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_OUT_INTERIM_PAGE");
            }
            if (this.isScreenshotPreviewMode) {
                pages.push("ENROLLMENT_PARTICIPATION_SIGNATURE_SCREENSHOT_PREVIEW_DECLINED");
            }
            pages.push("ENROLLMENT_DECLINED");
        }
        return pages;
    }
    get pages() {
        var _a;
        return (_a = this.enrollmentPages) !== null && _a !== void 0 ? _a : [];
    }
    get progressBarPercentage() {
        if (this.currentPageIndex === null || this.pages === NOT_LOADED)
            return 0;
        const pagesLength = this.isScreenshotPreviewMode
            ? this.pages.filter((page) => !this.checkIfInterimPage(page)).length
            : this.pages.length;
        const onePagePercentage = 100 / pagesLength;
        return (this.currentPageIndex + 1) * onePagePercentage;
    }
    get consentingPatientInfo() {
        var _a;
        if (this.authManager.userType === UserType.PATIENT) {
            return this.authManager.consentingPatientInfo;
        }
        else if (this.isProvider &&
            this._session.guidedConsentPatientSearchResult !== NOT_LOADED &&
            this._session.guidedConsentPatientSearchResult) {
            const patient = this._session.guidedConsentPatientSearchResult.patient;
            if (!patient) {
                console.warn(`No patient info found`);
                return null;
            }
            const state = usaStateFromName(patient.state);
            if (!state)
                return null;
            let isUnderage = false;
            if (patient.birthDate) {
                const age = differenceInYears(new Date(), patient.birthDate);
                isUnderage = this.stateLawsService.isUnderage(state, age);
            }
            return {
                id: patient.healthExPatientId,
                orgType: UserType.HEALTHCARE_ORGANIZATION,
                mrn: patient.mrn,
                name: getFullName(patient),
                isUnderage,
                state,
                language: (_a = patient.language) !== null && _a !== void 0 ? _a : "en",
            };
        }
        return null;
    }
    get homePageAfterConsent() {
        if (this.isIAS)
            return NamedRoutes.PATIENT_MY_HEALTHEX;
        return NamedRoutes.PATIENT_STUDIES;
    }
    async reAuth() {
        this.authManager.logout();
        this.reset();
        await this.refreshStudyAndPatientInfo();
        this.goToPage("ENROLLMENT_LOGIN");
    }
    reset() {
        const consentFlowPageData = this._session._consentFlowPageData;
        const guidedConsentCloseRoute = this._session.guidedConsentCloseRoute;
        this._session = new PatientConsentSession();
        this._session._consentFlowPageData = consentFlowPageData;
        this._session.guidedConsentCloseRoute = guidedConsentCloseRoute;
        this.browserLanguageUIManager.setInMemoryLanguage(null);
    }
    saveConsentSession() {
        const sessionSnapshot = instanceToPlain(this._session);
        this.persistence.setPatientConsentSession(sessionSnapshot);
        const inMemoryLanguage = this.browserLanguageUIManager.getInMemoryLanguage();
        this.persistence.setInMemoryLanguage(inMemoryLanguage);
    }
    restoreConsentSession() {
        const sessionSnapshot = this.persistence.getPatientConsentSession();
        if (sessionSnapshot) {
            this._session = plainToInstance(PatientConsentSession, sessionSnapshot);
            this.persistence.setPatientConsentSession(null);
        }
        const inMemoryLanguage = this.persistence.getInMemoryLanguage();
        if (inMemoryLanguage) {
            this.browserLanguageUIManager.setInMemoryLanguage(inMemoryLanguage);
            this.persistence.setInMemoryLanguage(null);
        }
    }
    completeStateRestoration() {
        this.saveDocumentRetrievalUIState(null);
        this._session.patientConsentSessionState = PatientConsentSessionState.INITIALIZED;
    }
    async restoreDocumentRetrievalState() {
        const batchId = this._session.retrievalBatchId;
        if (batchId) {
            const batch = await this.documentRetrievalManager.refreshBatch(batchId);
            if ((batch === null || batch === void 0 ? void 0 : batch.batchId) !== batchId) {
                console.warn(`Batch mismatch while restoring consent session`);
            }
        }
        else {
            console.warn(`No batch id found while restoring consent session`);
        }
    }
    async loginToLocationAndSaveState(locationKey, uiState) {
        var _a;
        if (!this.retrieval)
            return;
        const location = this.retrieval.locations.find((loc) => loc.id === locationKey);
        if (!location) {
            console.warn(`Location not found: ${locationKey}`);
            return;
        }
        if (location.authType !== DocumentRetrievalAuthType.FACILITATED_FHIR_EPIC) {
            console.warn(`Unexpected auth type: ${location.authType}`);
        }
        if (((_a = location.organizationMetadata) === null || _a === void 0 ? void 0 : _a.authType) !== DocumentRetrievalAuthType.FACILITATED_FHIR_EPIC) {
            console.warn(`No organization metadata`);
            return;
        }
        try {
            const batchId = this.retrieval.batchId;
            const fhirEndpoint = location.organizationMetadata.fhirEndpoint;
            const redirectUrl = this.facilitatedFHIRAuthManager.getRedirectUri();
            const authType = location.authType;
            const urlAndState = await this.documentRetrievalManager.getFacilitatedFHIRAuthUrl(batchId, fhirEndpoint, redirectUrl, authType);
            if (!urlAndState) {
                console.warn(`Failed to get auth URL`);
                this.facilitatedFHIRAuthManager.handleAuthError(FacilitatedFHIRAuthErrorReason.AUTH_FAILED, locationKey);
                return;
            }
            this.saveConsentSession();
            this.saveDocumentRetrievalUIState(instanceToPlain(uiState));
            await this.facilitatedFHIRAuthManager.redirectToFacilitatedFHIRLoginPage(locationKey, urlAndState.url, urlAndState.state, window.location.hash);
        }
        catch (error) {
            console.warn(`Failed to get auth URL: ${error}`);
            this.facilitatedFHIRAuthManager.handleAuthError(FacilitatedFHIRAuthErrorReason.AUTH_FAILED, locationKey);
        }
    }
    async handleFacilitatedFHIRAuthSuccess(key, code) {
        var _a;
        try {
            if (!this.retrieval) {
                console.warn("No retrieval found");
                return;
            }
            const location = this.retrieval.locations.find((loc) => loc.id === key);
            if (!location) {
                console.warn(`Location not found: ${key}`);
                return;
            }
            if (((_a = location.organizationMetadata) === null || _a === void 0 ? void 0 : _a.authType) !== DocumentRetrievalAuthType.FACILITATED_FHIR_EPIC) {
                console.warn(`No organization metadata`);
                return;
            }
            await this.documentRetrievalManager.getFacilitatedFHIRRecords(this.retrieval.batchId, location.id, location.organizationMetadata.fhirEndpoint, location.authType, this.facilitatedFHIRAuthManager.getRedirectUri(), code);
        }
        catch (error) {
            console.error("Error handling facilitated FHIR auth success: ", error);
        }
    }
    processFacilitatedFHIRAuthState() {
        if (this.facilitatedFHIRAuthManager.hasCompletedAuth) {
            this.restoreConsentSession();
            this._session.patientConsentSessionState = PatientConsentSessionState.RESTORING;
        }
        if (this.facilitatedFHIRAuthManager.state.state === FacilitatedFHIRAuthState.SUCCESS) {
            const { key, code } = this.facilitatedFHIRAuthManager.state;
            void this.restoreDocumentRetrievalState()
                .then(() => this.handleFacilitatedFHIRAuthSuccess(key, code))
                .then(() => {
                this._session.patientConsentSessionState = PatientConsentSessionState.RESTORING_COMPLETED;
            })
                .catch((error) => {
                console.error("Error handling facilitated FHIR auth success: ", error);
            });
        }
        else if (this.facilitatedFHIRAuthManager.state.state === FacilitatedFHIRAuthState.ERROR) {
            void this.restoreDocumentRetrievalState().then(() => {
                this._session.patientConsentSessionState = PatientConsentSessionState.RESTORING_COMPLETED;
            });
        }
    }
    saveDocumentRetrievalUIState(uiState) {
        this.persistence.setDocumentRetrievalUIState(uiState);
    }
    getDocumentRetrievalUIState() {
        return this.persistence.getDocumentRetrievalUIState();
    }
    getEnrollmentPagesViewed() {
        if (this.enrollmentPages === NOT_LOADED)
            return NOT_LOADED;
        if (!this.enrollmentPages)
            return null;
        const pagesViewed = [...this.enrollmentPages];
        if ((this.isIAS || this.isPDDAStudy) && this._session.documentRetrievalLocations.length > 0) {
            const locationPageIndex = pagesViewed.indexOf("ENROLLMENT_PDDA_RECORD_LOCATION");
            if (locationPageIndex !== -1) {
                pagesViewed.splice(locationPageIndex + 1, 0, "ENROLLMENT_PDDA_RECORD_LOCATION_SCREENSHOT_REVIEW_AND_SHARE");
            }
        }
        if (this.isIAS) {
            const confirmedPageIndex = pagesViewed.indexOf("ENROLLMENT_CONFIRMED");
            if (confirmedPageIndex !== -1) {
                pagesViewed.splice(confirmedPageIndex, 1);
            }
        }
        return pagesViewed;
    }
    async authorizeConsent(isConsented, input, allowNavigationAfterConsentConfirmation = true) {
        if (this.consentExpirationDate === null) {
            return;
        }
        if (!this.consentingPatientInfo) {
            return;
        }
        const pagesViewed = this.getEnrollmentPagesViewed();
        if (pagesViewed === NOT_LOADED) {
            return;
        }
        this._session.isSubmittingConsent = true;
        this._session.hasConsentBeenSubmittedThisFlow = true;
        const consentInput = isConsented
            ? {
                expirationDate: this.consentExpirationDate,
                consentUIFlow: ConsentUIFlow.PATIENT_DIRECTED_DATA_EXCHANGE,
                uiHash: getUIHash(),
                pagesViewed,
                consentedByLink: this.consentedByLink,
                providerId: this.providerId,
                ...input,
            }
            : {
                expirationDate: this.consentExpirationDate,
                consentedByLink: this.consentedByLink,
                providerId: this.providerId,
                ...input,
            };
        try {
            if (isConsented) {
                if (!this.isInteractivePreview && !this.isScreenshotMode) {
                    if (this.isIAS) {
                        await this.iasManager.optInToIAS(this.consentingPatientInfo, consentInput);
                    }
                    else {
                        if (this._session.study === NOT_LOADED || this._session.study === null) {
                            throw new Error(`Unsupported no study for enrollment flow`);
                        }
                        await this.studyManager.optInToStudy(this.consentingPatientInfo, this._session.study, getConsentTypesForProject(this._session.study, !!this.allowsIdentifiedFutureDataUse), consentInput);
                    }
                    this._session.hasAuthorizedConsentEnrollment = true;
                    if (allowNavigationAfterConsentConfirmation)
                        this.goToNextPage();
                }
            }
            else {
                if (!this.isInteractivePreview && !this.isScreenshotMode) {
                    if (this.isIAS) {
                        await this.iasManager.optOutOfIAS(this.consentingPatientInfo, consentInput);
                    }
                    else {
                        if (this._session.study === NOT_LOADED || this._session.study === null) {
                            throw new Error(`Unsupported no study for enrollment flow`);
                        }
                        await this.studyManager.optOutOfStudy(this.consentingPatientInfo, this._session.study, getConsentTypesForProject(this._session.study, !!this.allowsIdentifiedFutureDataUse), consentInput);
                    }
                }
                this._session.hasAuthorizedConsentEnrollment = false;
                this.goToPage("ENROLLMENT_DECLINED");
            }
        }
        finally {
            this._session.isSubmittingConsent = false;
        }
    }
    _close(route) {
        const studyId = this._session.studyId;
        const isPreview = this.isInteractivePreview;
        const isProvider = this.isProvider;
        const guidedConsentPatientMRN = this._session.guidedConsentPatientMRN;
        const guidedConsentCloseRoute = this._session.guidedConsentCloseRoute;
        this.reset();
        if (!isPreview) {
            if (isProvider) {
                if (guidedConsentPatientMRN) {
                    if ((guidedConsentCloseRoute === null || guidedConsentCloseRoute === void 0 ? void 0 : guidedConsentCloseRoute.routeName) === NamedRoutes.PATIENTS_STUDY_DETAILS.routeName && studyId) {
                        this.routingUIManager.navigateTo(NamedRoutes.PATIENTS_STUDY_DETAILS, {
                            studyId,
                            mrn: guidedConsentPatientMRN,
                        });
                    }
                    else {
                        this.routingUIManager.navigateTo(NamedRoutes.PATIENTS_SEARCH, {
                            mrn: guidedConsentPatientMRN,
                        });
                    }
                }
                else {
                    this.routingUIManager.navigateTo(NamedRoutes.PATIENTS);
                }
            }
            else if (route === NamedRoutes.PATIENT_STUDIES) {
                this.patientResearchDashboardUIManager.goToPage(MobileBottomNavigationButtonType.RESEARCH, PatientStudiesListViewTab.ALL);
            }
            else {
                this.routingUIManager.navigateTo(route);
            }
        }
    }
    close() {
        this._close(this.homePageAfterConsent);
    }
    done() {
        this._close(this.homePageAfterConsent);
    }
    goToPage(route) {
        if (this.isInteractivePreview) {
            this._session.currentInteractivePreviewRoute = route;
        }
        else {
            const result = this.patientConsentManagerRouteToRoute(route);
            if (result) {
                this.routingUIManager.navigateTo(result.route, { ...result.routeParams });
                void this.saveConsentFlowProgress();
            }
        }
    }
    goToPreviousPage() {
        if (this.currentPageIndex === null || this.pages === NOT_LOADED) {
            return;
        }
        if (this.currentPageIndex <= 0) {
            this.close();
            return;
        }
        let targetPageIndex = this.currentPageIndex - 1;
        const validPages = [];
        const invalidPages = [];
        if (this.isPDDAGeneralDataUse) {
            if (this.pages[this.currentPageIndex] === "ENROLLMENT_DECLINED") {
                validPages.push("ENROLLMENT_WELCOME");
            }
            else {
                invalidPages.push("ENROLLMENT_REQUEST_DETAILS_POPUP");
                if (this.isPatientLoggedIn) {
                    invalidPages.push("ENROLLMENT_LOGIN");
                }
            }
        }
        if (invalidPages.length > 0) {
            while (invalidPages.includes(this.pages[targetPageIndex])) {
                if (targetPageIndex <= 0) {
                    break;
                }
                targetPageIndex -= 1;
            }
        }
        if (validPages.length > 0) {
            while (!validPages.includes(this.pages[targetPageIndex])) {
                if (targetPageIndex <= 0) {
                    break;
                }
                targetPageIndex -= 1;
            }
        }
        this.goToPage(this.pages[targetPageIndex]);
    }
    goToNextPage() {
        if (this.currentPageIndex === null || this.pages === NOT_LOADED) {
            return;
        }
        let targetPageIndex = this.currentPageIndex + 1;
        if (this.currentPageIndex === this.pages.length - 1) {
            if (this.isProvider) {
                this.routingUIManager.navigateTo(NamedRoutes.PATIENTS);
            }
            else {
                this.routingUIManager.navigateTo(NamedRoutes.PATIENT_STUDIES);
            }
            return;
        }
        if (this.isPDDAGeneralDataUse) {
            while (this.pages[targetPageIndex] === "ENROLLMENT_REQUEST_DETAILS_POPUP") {
                if (targetPageIndex === this.pages.length - 1) {
                    break;
                }
                targetPageIndex += 1;
            }
        }
        this.goToPage(this.pages[targetPageIndex]);
    }
    refreshPage() {
        if (this.currentPage !== null)
            this.goToPage(this.currentPage);
    }
    showPopup(route) {
        this._session.routeBeforePopup = this.currentPatientConsentRoute;
        this.goToPage(route);
    }
    hidePopupIfPresent() {
        if (!this._session.routeBeforePopup)
            return false;
        const routeToReturnTo = this._session.routeBeforePopup;
        this._session.routeBeforePopup = null;
        this.goToPage(routeToReturnTo);
        return true;
    }
    patientConsentManagerRouteToRoute(route) {
        const { flow, step } = patientConsentRouteParts(route);
        const link = this.pathLink ? slugify(this.pathLink) : undefined;
        if (flow === PatientConsentFlow.ENROLLMENT) {
            if (this._session.guidedConsentPatientMRN) {
                return {
                    route: NamedRoutes.GUIDED_PATIENT_CONSENT_ENROLLMENT,
                    routeParams: {
                        studyId: this._session.studyId,
                        mrn: this._session.guidedConsentPatientMRN,
                        step,
                        ...(this._session.guidedConsentLink ? { link: "link" } : {}),
                    },
                };
            }
            else if (this.isPrereadEnrollment) {
                return {
                    route: NamedRoutes.PREREAD_PATIENT_CONSENT_ENROLLMENT,
                    routeParams: { studyId: this._session.studyId, step },
                };
            }
            else if (this.isIAS) {
                if (this.isDataUpdate) {
                    return {
                        route: NamedRoutes.PATIENT_IAS_DATA_UPDATE,
                        routeParams: { step, link },
                    };
                }
                else {
                    return {
                        route: NamedRoutes.PATIENT_IAS_CONSENT,
                        routeParams: { step, link },
                    };
                }
            }
            else if (this.isDataUpdate) {
                return {
                    route: NamedRoutes.PATIENT_CONSENT_DATA_UPDATE,
                    routeParams: { studyId: this._session.studyId, step, link },
                };
            }
            else {
                return {
                    route: NamedRoutes.PATIENT_CONSENT_ENROLLMENT,
                    routeParams: { studyId: this._session.studyId, step, link },
                };
            }
        }
        else {
            return null;
        }
    }
    routeToPatientConsentRoute(route, routeParams) {
        if (!route) {
            return null;
        }
        const step = routeParams === null || routeParams === void 0 ? void 0 : routeParams.step;
        if (!step) {
            return null;
        }
        if (route.routeName === NamedRoutes.PATIENT_CONSENT_ENROLLMENT.routeName ||
            route.routeName === NamedRoutes.GUIDED_PATIENT_CONSENT_ENROLLMENT.routeName ||
            route.routeName === NamedRoutes.PREREAD_PATIENT_CONSENT_ENROLLMENT.routeName ||
            route.routeName === NamedRoutes.PATIENT_CONSENT_DATA_UPDATE.routeName ||
            route.routeName === NamedRoutes.PATIENT_IAS_CONSENT.routeName ||
            route.routeName === NamedRoutes.PATIENT_IAS_DATA_UPDATE.routeName) {
            const patientConsentManagerRoute = patientConsentRouteFromParts({
                flow: PatientConsentFlow.ENROLLMENT,
                step: unslugify(step),
            });
            if (patientConsentManagerRoute)
                return patientConsentManagerRoute;
        }
        return null;
    }
    goToSavedWelcomeBackPage() {
        if (this._session.continueWhereILeftOffRoute === "ENROLLMENT_REAUTHORIZATION") {
            this.goToPage("ENROLLMENT_INFORMED_CONSENT");
        }
        else if (this._session.continueWhereILeftOffRoute) {
            this.goToPage(this._session.continueWhereILeftOffRoute);
        }
        else {
            this.goToPage("ENROLLMENT_WELCOME");
        }
    }
    startFromBeginning() {
        this.goToPage("ENROLLMENT_WELCOME");
    }
    async saveConsentFlowProgress() {
        var _a, _b, _c;
        if (!this.authManager.orgId ||
            !((_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id) ||
            this._session.study === NOT_LOADED ||
            this._session.study === null ||
            !this.currentPage ||
            this.isPrereadEnrollment)
            return;
        if (invalidPagesForConsentFlowProgressUpdates.includes(this.currentPage))
            return;
        try {
            const guidedConsentUpdate = this.isProvider
                ? { guidedConsentUserId: this.authManager.id }
                : {};
            let savedPage = (_b = this.currentPage) !== null && _b !== void 0 ? _b : "";
            if (["ENROLLMENT_WELCOME_BACK", "ENROLLMENT_EXCLUDED"].includes(savedPage)) {
                if (this._session.continueWhereILeftOffRoute) {
                    savedPage = this._session.continueWhereILeftOffRoute;
                }
                else {
                    savedPage = "ENROLLMENT_WELCOME";
                }
            }
            if (this.isPDDAGeneralDataUse) {
                const excludedPagesFromSavingList = [
                    "ENROLLMENT_WELCOME",
                    "ENROLLMENT_REQUEST_DETAILS_POPUP",
                    "ENROLLMENT_LOGIN",
                    "ENROLLMENT_IDENTITY_VERIFIED",
                ];
                if (excludedPagesFromSavingList.includes(savedPage)) {
                    if (this._session.continueWhereILeftOffRoute) {
                        if (excludedPagesFromSavingList.includes(this._session.continueWhereILeftOffRoute)) {
                            savedPage = "";
                            return;
                        }
                        else {
                            savedPage = this._session.continueWhereILeftOffRoute;
                        }
                    }
                    else {
                        savedPage = "";
                        return;
                    }
                }
            }
            await this.patientService.updateConsentFlowProgress(this.authManager.orgId, this.consentingPatientInfo.id, {
                savedPage,
                studyId: this._session.study.id,
                studyVersionId: (_c = this.studyVersionId) !== null && _c !== void 0 ? _c : null,
                consentUIFlow: ConsentUIFlow.PATIENT_DIRECTED_DATA_EXCHANGE,
                consentEnabled: this._session.patientInformedConsentEnabled,
                consentedByLink: this.consentedByLink,
                interested: this._session.isInterested,
                additionalParentGuardianExemptSignature: this._session.additionalParentGuardianExemptSignature,
                minorParticipantExemptFromAssent: this._session.minorParticipantExemptFromAssent,
                larParticipantExemptSignature: this._session.larParticipantExemptSignature,
                witnessSkipSignature: this._session.witnessSkipSignature,
                isDataUpdate: this.isDataUpdate,
                ...guidedConsentUpdate,
            });
        }
        catch (e) {
            console.warn("Failed to update consent flow progress");
        }
    }
    updatePatientInformedConsentEnabled(isConsented, isInterested) {
        this._session.patientInformedConsentEnabled = isConsented;
        this._session.isInterested = isInterested;
    }
    updateUserSignatureHIPAA(patientSignatureHIPAA) {
        this._session.patientSignatureHIPAA = patientSignatureHIPAA;
    }
    updateUserSigningReasonHIPAA(signingReason) {
        this._session.patientSigningReasonHIPAA = signingReason;
    }
    updateProviderSignature(providerSignature) {
        this._session.providerSignature = providerSignature;
    }
    updateAdditionalParentGuardianExemptSignature(additionalParentGuardianExemptSignature) {
        this._session.additionalParentGuardianExemptSignature = additionalParentGuardianExemptSignature;
    }
    updateMinorParticipantExemptFromAssent(minorParticipantExemptFromAssent) {
        this._session.minorParticipantExemptFromAssent = minorParticipantExemptFromAssent;
    }
    updateParentGuardianSignature(parentGuardianSignature) {
        this._session.parentGuardianSignature = parentGuardianSignature;
    }
    updateParentGuardianSigningReason(parentGuardianSigningReason) {
        this._session.parentGuardianSigningReason = parentGuardianSigningReason;
    }
    updateAdditionalParentGuardianSignature(additionalParentGuardianSignature) {
        this._session.additionalParentGuardianSignature = additionalParentGuardianSignature;
    }
    updateAdditionalParentGuardianSigningReason(additionalParentGuardianSigningReason) {
        this._session.additionalParentGuardianSigningReason = additionalParentGuardianSigningReason;
    }
    updateMinorParticipantSignature(minorParticipantSignature) {
        this._session.minorParticipantSignature = minorParticipantSignature;
    }
    updateWitnessSkipSignature(witnessSkipSignature) {
        this._session.witnessSkipSignature = witnessSkipSignature;
    }
    updateWitnessSignature(witnessSignature) {
        this._session.witnessSignature = witnessSignature;
    }
    updateDocumentRetrievalLocations(documentRetrievalLocations) {
        this._session.documentRetrievalLocations = documentRetrievalLocations;
    }
    updateContactMethod(contactMethod, contactEmail, contactPhone) {
        this._session.contactMethod = contactMethod;
        this._session.contactEmail = contactEmail;
        this._session.contactPhone = contactPhone;
    }
    setHasDeclinedBeforeLogin() {
        this._session.hasDeclinedBeforeLogin = true;
    }
    setHasLoggedInDuringConsent() {
        this._session.hasLoggedInDuringConsent = true;
    }
    async declineConsent() {
        if (this.patientConsentFlow === PatientConsentFlow.ENROLLMENT) {
            this.updatePatientInformedConsentEnabled(false, null);
        }
        if (this.isInteractivePreview || this.isScreenshotPreviewMode || this.isScreenshotMode || this.isDataUpdate) {
            this.goToPage("ENROLLMENT_DECLINED");
            return;
        }
        if (this.isPatientLoggedIn) {
            await this.authorizeConsent(false, {});
        }
        else {
            this.setHasDeclinedBeforeLogin();
            this.goToPage("ENROLLMENT_DECLINED");
        }
    }
    async confirmConsentOrGoToNextPage() {
        if (this.isInteractivePreview || this.isScreenshotPreviewMode || this.isScreenshotMode || this.isDataUpdate) {
            this.goToNextPage();
            return;
        }
        let possibleLastPages;
        if (this.patientConsentFlow === PatientConsentFlow.ENROLLMENT) {
            possibleLastPages = [
                "ENROLLMENT_PARTICIPATION_SIGNATURE",
                "ENROLLMENT_PARENT_GUARDIAN_SIGNATURE",
                "ENROLLMENT_ADDITIONAL_PARENT_GUARDIAN_SIGNATURE",
                "ENROLLMENT_PASS_DEVICE_TO_MINOR",
                "ENROLLMENT_MINOR_SIGNATURE",
                "ENROLLMENT_WITNESS_SIGNATURE",
                "ENROLLMENT_PROVIDER_AUTHORIZATION",
                "ENROLLMENT_PDDA_RECORD_LOCATION",
            ];
        }
        else {
            throw new Error("Invalid consent type");
        }
        if (this.currentPage !== null && this.pages !== NOT_LOADED && possibleLastPages.includes(this.currentPage)) {
            const lastPageBeforeConsent = [...this.pages].reverse().find((page) => possibleLastPages.includes(page));
            if (lastPageBeforeConsent === this.currentPage) {
                await this.confirmConsent(true);
                return;
            }
        }
        this.goToNextPage();
    }
    async initiateDocumentRetrieval(studyId) {
        this._session.retrievalBatchId = await this.documentRetrievalManager.initiateDocumentRetrieval(studyId, this.isDataUpdate);
        if (!this._session.retrievalBatchId && this.isDataUpdate) {
            this._session.retrievalBatchId = await this.documentRetrievalManager.initiateDocumentRetrieval(studyId, false);
        }
    }
    async confirmConsent(allowNavigationAfterConsentConfirmation) {
        var _a, _b;
        if (this.patientConsentFlow === PatientConsentFlow.ENROLLMENT) {
            this.updatePatientInformedConsentEnabled(true, null);
        }
        const consentInput = {
            patientName: this.patientName,
            patientMRN: this.patientMRN,
            signerName: this._session.patientSignatureHIPAA,
            signingReason: this._session.patientSigningReasonHIPAA,
            parentGuardianSignerName: this._session.parentGuardianSignature,
            parentGuardianSigningReason: this._session.parentGuardianSigningReason,
            additionalParentGuardianSignerName: this._session.additionalParentGuardianSignature,
            additionalParentGuardianSigningReason: this._session.additionalParentGuardianSigningReason,
            additionalParentGuardianExemptSignature: this._session.additionalParentGuardianExemptSignature,
            minorParticipantSignerName: this._session.minorParticipantSignature,
            minorParticipantExemptFromAssent: this._session.minorParticipantExemptFromAssent,
            witnessSkipSignature: this._session.witnessSkipSignature,
            witnessSignerName: this._session.witnessSignature,
            isProxyAccess: this.isProxyAccess,
            providerSignerName: this.isProvider ? this.providerName : null,
            mp4Url: (_b = (_a = this.videoTranscode) === null || _a === void 0 ? void 0 : _a.mp4Url) !== null && _b !== void 0 ? _b : null,
            posterUrl: getPosterUrl(this.videoTranscode),
            documentRetrievalLocations: this._session.documentRetrievalLocations,
            loggedInDuringConsent: this._session.hasLoggedInDuringConsent,
            retrieval: this.retrieval,
        };
        await this.authorizeConsent(true, consentInput, allowNavigationAfterConsentConfirmation);
    }
    async confirmImInterested() {
        if (this.isInteractivePreview || this.isScreenshotPreviewMode || this.isScreenshotMode) {
            this.goToNextPage();
            return;
        }
        if (!this._session.studyId)
            return;
        if (!this.consentingPatientInfo)
            return;
        await this.studyManager.updateIdentifiedPatientImInterested(this._session.studyId, this.consentingPatientInfo);
        this.goToNextPage();
    }
    startConsentFlow(studyId, flow, mrn, link) {
        this.reset();
        this._session.guidedConsentPatientMRN = mrn;
        this._session.studyId = studyId;
        this._session.guidedConsentLink = link;
        this.goToPage("ENROLLMENT_WELCOME");
    }
    dataUpdateConsentFlow(studyId) {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_CONSENT_DATA_UPDATE, { studyId });
    }
    iasConsentFlow() {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_CONSENT);
    }
    iasDataUpdateConsentFlow() {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_DATA_UPDATE);
    }
    getConsentExpirationFromStudy(study) {
        var _a;
        return this.consentExpirationService.getConsentExpiration(study.completionDate, (_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.state);
    }
    getConsentFlowWaitForSelector(step) {
        if (step === "ENROLLMENT_HIPAA_AUTHORIZATION") {
            return { selector: `.${hipaaContentLoadedClassName}`, strategy: ScreenshotWaitStrategy.WAIT_FOR_SELECTOR };
        }
        else {
            return {
                selector: [
                    { selector: `#${patientConsentStepParentId}`, strategy: ScreenshotWaitStrategy.WAIT_FOR_SELECTOR },
                    { selector: `.${screenshotResourceLoadingClass}`, strategy: ScreenshotWaitStrategy.WAIT_FOR_ALL_REMOVED },
                ],
            };
        }
    }
    getConsentFlowScreenshotHeight(step) {
        if (this.checkIfInterimPage(step))
            return 50;
        return undefined;
    }
    async getConsentFlowUrls(studyId, studyVersionId, previewMetadata, pagesViewed) {
        const rootURL = `${window.location.origin}/#/patient-consent/${studyId !== null && studyId !== void 0 ? studyId : this.iasPath}`;
        let pages;
        if (this.isScreenshotPreviewMode && previewMetadata) {
            await this.startInteractivePreview(studyId !== null && studyId !== void 0 ? studyId : "", studyVersionId !== null && studyVersionId !== void 0 ? studyVersionId : "", previewMetadata);
            const previewPages = this.pages;
            if (previewPages !== NOT_LOADED)
                pages = previewPages;
        }
        else {
            pages = pagesViewed
                ?
                    pagesViewed.filter((pageViewed) => includes(patientConsentRouteValues, pageViewed))
                : patientConsentRouteValues;
        }
        return ((pages !== null && pages !== void 0 ? pages : [])
            .map((route) => ({ route, ...patientConsentRouteParts(route) }))
            .map((parts) => {
            const waitForInfo = this.getConsentFlowWaitForSelector(parts.route);
            return {
                name: parts.route,
                url: `${rootURL}/${slugify(parts.flow)}/${slugify(parts.step)}`,
                waitForSelector: waitForInfo.selector,
                waitStrategy: waitForInfo.strategy,
                height: this.getConsentFlowScreenshotHeight(parts.route),
            };
        }));
    }
    reconsent() {
        if (this.pages !== NOT_LOADED) {
            const pages = this.pages;
            const lastExcludedIndex = Math.max(-1, ...this.excludedPagesForReconsent.map((page) => pages.indexOf(page)));
            const firstPageForReconsent = pages[lastExcludedIndex + 1];
            if (firstPageForReconsent) {
                this.goToPage(firstPageForReconsent);
            }
        }
    }
    async trackEvent(event) {
        var _a, _b, _c, _d, _e;
        if (this.isInteractivePreview)
            return;
        let category;
        if (this._session.study !== NOT_LOADED && this._session.study) {
            const consentType = getPrimaryConsentTypeForProject(this._session.study);
            category = mapConsentTypeToEventCategory(consentType);
        }
        else {
            category = this.isPrereadEnrollment ? EventCategory.PREREAD_FLOW : EventCategory.PATIENT_DIRECTED_DATA_EXCHANGE;
        }
        const metadata = {
            userType: this.isProvider ? "PROVIDER" : UserType.PATIENT,
            flowType: this.isProvider ? EnrollmentFlowType.GUIDED : EnrollmentFlowType.SELF_SERVE,
            userId: (_c = (_b = (_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : this.authManager.id) !== null && _c !== void 0 ? _c : "",
            page: (_d = this.currentPage) !== null && _d !== void 0 ? _d : undefined,
        };
        await this.analyticsService.trackEvent({
            studyId: this._session.studyId,
            category: (_e = event.category) !== null && _e !== void 0 ? _e : category,
            action: event.action,
            metadata,
        });
    }
    patientConsentFlowFromPreviewConsentFlow(previewConsentFlow) {
        if (!previewConsentFlow)
            return null;
        return previewConsentFlow === PreviewConsentFlow.PATIENT_DIRECTED_DATA_EXCHANGE
            ? PatientConsentFlow.ENROLLMENT
            : willNeverHappen(previewConsentFlow);
    }
    reactiveRedirect(route, handler) {
        reaction(() => [this.routingUIManager.currentRoute, this.routingUIManager.currentRouteParams], async ([currentRoute, _currentRouteParams]) => {
            if (!routeEquals(currentRoute, route))
                return;
            if (!this.routingUIManager.currentRouteParams)
                return;
            const result = await handler(this.routingUIManager.currentRouteParams);
            if (result) {
                this.routingUIManager.redirectTo(result.route, result.routeParams);
            }
        });
    }
    get urlParams() {
        const queryParams = effectiveQueryParams();
        const oauthState = queryParams.get("oauth_state");
        return { oauthState };
    }
    handleOAuthParams() {
        this.restoreOAuthParamsFromStorage();
        try {
            if (!this.urlParams.oauthState) {
                console.log("No oauthState in URL, keeping existing oauthParams:", this.oauthParams);
                return;
            }
            const decodedState = atob(this.urlParams.oauthState);
            this.oauthParams = JSON.parse(decodedState);
            this.persistOAuthParamsToStorage();
        }
        catch (e) {
            console.error("Failed to parse OAuth state:", e);
            if (!this.oauthParams) {
                this.oauthParams = null;
            }
        }
    }
    persistOAuthParamsToStorage() {
        if (this.oauthParams) {
            try {
                localStorage.setItem(this.oauthParamsKey, JSON.stringify(this.oauthParams));
            }
            catch (e) {
                console.warn("Failed to persist OAuth params to localStorage:", e);
            }
        }
    }
    restoreOAuthParamsFromStorage() {
        try {
            const stored = localStorage.getItem(this.oauthParamsKey);
            if (stored) {
                this.oauthParams = JSON.parse(stored);
            }
        }
        catch (e) {
            console.warn("Failed to restore OAuth params from localStorage:", e);
            this.oauthParams = null;
        }
    }
    clearOAuthParamsFromStorage() {
        try {
            localStorage.removeItem(this.oauthParamsKey);
        }
        catch (e) {
            console.warn("Failed to clear OAuth params from localStorage:", e);
        }
    }
    get isAIFlow() {
        var _a, _b;
        return (this.environment.OPENAI_AI_ENABLEMENT_PROJECT_ID === ((_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.studyId) ||
            this.environment.ANTHROPIC_AI_ENABLEMENT_PROJECT_ID === ((_b = this.routingUIManager.currentRouteParams) === null || _b === void 0 ? void 0 : _b.studyId));
    }
    get isOAuthFlow() {
        return this.oauthParams !== null && this.isAIFlow;
    }
    async completeOAuthAfterPDDE() {
        var _a, _b, _c;
        if (!this.isOAuthFlow) {
            console.warn("No OAuth params available for completion");
            return;
        }
        const oauthParams = { ...this.oauthParams };
        try {
            this.clearOAuthParamsFromStorage();
            this.oauthParams = null;
            const queryParams = {
                ...oauthParams,
                userId: (_b = (_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : this.authManager.id,
                organizationId: this.authManager.orgId,
                userType: (_c = this.authManager.userType) !== null && _c !== void 0 ? _c : UserType.PATIENT,
                claims: {
                    firstName: this.authManager.firstName,
                    lastName: this.authManager.lastName,
                    state: this.authManager.state,
                },
            };
            const camelToSnakeCase = (str) => str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
            const filteredQueryParams = Object.fromEntries(Object.entries(queryParams)
                .filter(([_, value]) => value !== null)
                .map(([key, value]) => [
                camelToSnakeCase(key),
                key === "claims" && typeof value === "object" ? JSON.stringify(value) : String(value),
            ]));
            const params = new URLSearchParams(filteredQueryParams);
            const completeUrl = `${this.environment.API_BASE_URL}/oauth/complete?${params.toString()}`;
            window.location.href = completeUrl;
        }
        catch (error) {
            console.error("Error completing OAuth flow:", error);
            const fallbackUrl = `${oauthParams.redirectUri}?error=redirect_error&error_description=OAuth redirect failed&state=${oauthParams.state}`;
            window.location.href = fallbackUrl;
        }
    }
    async getDownloadPdfLink() {
        var _a;
        if (!((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.batchId)) {
            console.warn(`No batch available`);
            return null;
        }
        return await this.documentRetrievalManager.getDownloadPdfLink(this.retrieval.batchId);
    }
    async getDownloadRawDocumentsLink() {
        var _a;
        if (!((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.batchId)) {
            console.warn(`No batch available`);
            return null;
        }
        return await this.documentRetrievalManager.getDownloadDocumentsLink(this.retrieval.batchId);
    }
}
__decorate([
    observable,
    __metadata("design:type", PatientConsentSession)
], PatientConsentUIManager.prototype, "_session", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "studyVersionId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isDataUpdate", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isIAS", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "recordsSummary", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isPatientLoggedIn", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "consentFlowPageData", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "studyType", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isInteractivePreview", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isScreenshotMode", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isScreenshotPreviewMode", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isPopup", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "previewMetadata", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isFirstRoute", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "currentPatientConsentRoute", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "patientConsentFlow", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "patientName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isParentGuardian", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "parentGuardianName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "additionalParentGuardianName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "patientMRN", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "providerName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "patientFriendlyTitle", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "studySectionImages", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "patientPortalName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "studyInfo", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "studySponsor", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "studyPrimaryContactName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "studyPrimaryContactTitle", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "primaryDiseaseOrConditions", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "patientFriendlySummary", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "irbApprovalStamp", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "primaryContactEmail", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "primaryContactPhone", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "risksOfEnrollment", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "benefitsOfEnrollment", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "alternativesToParticipation", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "resourceScopes", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "sensitivityScopes", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isFullRecordMode", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "consentExpirationDate", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isPDDAGeneralDataUse", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "showClassicLoginPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "rightToBeContacted", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "futureUseOfDataText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "showBillOfRightsPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "allowsIdentifiedFutureDataUse", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "showSensitiveDataConfirmationPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isPDDAStudy", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "pddaHealthRecordsUpdateFrequency", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "showTimeForVisitsPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "showEnrollmentDataSharingPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "showParticipationTerminationPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "showLeavingStudyEarlyPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "showCostOfParticipationPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "showSharingResultsPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "showCompensationPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "excludedReason", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "videoTranscode", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "require21CFRPart11ForInformedConsents", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "needsReauthorization", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isProvider", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "providerId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isPrereadEnrollment", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isProxyAccess", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isParentGuardianAuthRequired", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isMinorConsentEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isMinor", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "showMinorConsentPages", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "showParticipantConsentPages", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isAdditionalParentGuardianEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isInterimPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "studyICFTranslationAvailableLanguages", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "currentLanguage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "retrieval", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "pendingRetrieval", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "locatingPDDERecords", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "canAddLocations", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "zeroLocationsFound", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "hasPatientConsented", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "excludedPagesForReconsent", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "mayHaveAccessibleRecordsOnceReady", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isRelevantRoute", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "currentPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "pathLink", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "consentedByLink", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "currentPageIndex", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "hideStudyInformationStep", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "enrollmentPages", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "pddaGeneralDataUseEnrollmentPages", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "progressBarPercentage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "consentingPatientInfo", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "homePageAfterConsent", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "urlParams", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isAIFlow", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], PatientConsentUIManager.prototype, "isOAuthFlow", null);
